// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { liveQuery } from 'dexie';

import {
  appEventTypeForKind,
  buildMeetingCancelPayload,
  buildMeetingCreatePayload,
  buildMeetingRsvpPayload,
  buildMeetingUpdatePayload,
  type ChatMessageVariant,
  ChatRoomShellHandlersProvider,
  deriveVariantFromBody,
  EMPTY_MEETING_STATE,
  encodeEnvelopeJsonBytes,
  ForwardOverlay,
  gameEventsFromMessages,
  GamePickerOverlay,
  ImageLightbox,
  isGameKind,
  lastMessagePreview,
  type MeetingEvent,
  type MeetingRsvpResponse,
  type MeetingState,
  meetingStatus,
  type MentionCandidate,
  type Message,
  type MessageMenuAction,
  myRsvp as meetingMyRsvp,
  newMeetingId,
  parseEnvelope,
  persistSentMessage as sharedPersistSentMessage,
  type PollViewContent,
  type PollVoterAvatar,
  PollVotesPanel,
  reduceMeeting,
  RIGHT_PANEL_MAX_WIDTH,
  RIGHT_PANEL_MIN_WIDTH,
  rsvpCounts as meetingRsvpCounts,
  SidePanelFrame,
  type ThreadComposerProps,
  type ThreadGateway,
  type ThreadIdentifier,
  type ThreadOpenSnapshot,
  type ThreadParentPreview,
  type ThreadReply,
  type ThreadRowActions,
  useChatRoomShellViewModel,
  useConversations,
  useGameSessions,
  useReactions,
  useSession,
  useUiPreferencesStore,
} from '@ping/ui';
import { pingToast } from '@ping/ui';

import { setActiveNotificationConversation } from '@app/notifications/activeConversation';
import { useCallStore } from '@calls/store/callStore';
import { isFeatureEnabled } from '@core/featureFlags';
import { log } from '@core/log/log';
import {
  dedupeAllDirectConversations,
  deleteConversation,
  mute as muteConversation,
  unmute as unmuteConversation,
} from '@core/persistence/conversations';
import { db, webMeetingRemindersPersister, webPollVotesPersister,webReactionsPersister } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { ActivityPanel, useActivityUnreadCount } from '@features/activity';
import { MeetingComposerHost, PollComposerHost, ShareLocationHost, ThreadPanelHost } from '@features/chat';
import { ChatEmptyState } from '@features/chat/ChatEmptyState';
import { ConversationListPanel } from '@features/chat/ConversationListPanel';
import { fetchAndDecryptMediaBlob } from '@features/chat/fetchMediaBlob';
import { takePendingInvite } from '@features/chat/inviteLink';
import { addLocallyRemovedMessage } from '@features/chat/locallyRemovedMessages';
import { resolveCurrentLocation,searchPlaces } from '@features/chat/locationProviders';
import { hydrateMemberProfiles } from '@features/chat/memberProfileHydration';
import { getOrCreateMlsConversation } from '@features/chat/mlsConversation';
import { NewChatModal } from '@features/chat/NewChatModal';
import { NewGroupModal } from '@features/chat/NewGroupModal';
import { webConversationsPersister, webMessagesPersister } from '@features/chat/persistenceAdapters';
import {
  webPinnedPersister,
  webSavedPersister,
  webStarredPersister,
} from '@features/chat/personalBookmarks';
import { resolveActiveConv } from '@features/chat/resolveActiveConv';
import { parseFileEnvelope } from '@features/chat/sendFileMessage';
import { parseImageEnvelope } from '@features/chat/sendImageMessage';
import { parseVideoEnvelope } from '@features/chat/sendVideoMessage';
import { parseVoiceEnvelope } from '@features/chat/sendVoiceMessage';
import { SidePanelResizeHandle } from '@features/chat/SidePanelResizeHandle';
import { useThreadComposer } from '@features/chat/useThreadComposer';
import { WebChatRoom } from '@features/chat/WebChatRoom';
import { uploadImageSources } from '@features/chat/webImageUploadBridge';
import { markThreadReadPointer } from '@features/chat/webThreadGateway';
import {
  type MeetingDraft,
  MeetingModal,
  type MeetingModalInitial,
  MeetingResponsesModal,
} from '@features/meetings';
import { NotesPanelHost } from '@features/notes/NotesPanelHost';
import { ProfileOverlaysHost } from '@features/profile/ProfileOverlaysHost';
import { useSelfProfile } from '@features/profile/selfProfileStore';
import { sendStar } from '@features/tasks/deviceGroupTaskSync';
import { TasksPanelHost } from '@features/tasks/TasksPanelHost';
import { getCurrentTransport } from '@session/sessionBootstrap';
import { useRouter } from '@shell/RouterContext';

import { requestCreateOnNextMount } from './BusinessDirectoryRouteHost';
import { useChatRouteId } from './chatRouteUrl';

/**
 * Web chat route: 2-column layout matching Figma file
 * Y95LG1oFzU2PHIvFuMYTfJ → Chat page (12:15197) and the inner
 * ChatRoom frame (node 2804:25678).
 *  - Left  (340px): ConversationListPanel — Dexie-backed list panel
 *                   that delegates rendering to
 *                   `<ConversationListPanel>` from @ping/ui.
 *  - Right (rest):  WebChatRoom — Dexie/MLS host around
 *                   `<ChatRoom>` from @ping/ui (header +
 *                   message list + composer all in one shared
 *                   component), OR ChatEmptyState.
 *  - New chat is a typeahead modal — see NewChatModal — that talks
 *    to /v1/users/search + /v1/conversations/request.
 */
// Extremely-fast slide for the conversations↔activity swap.

export function ChatRouteHost(): JSX.Element {
  const router = useRouter();
  const activityUnreadCount = useActivityUnreadCount();
  const sidePanelWidth = useUiPreferencesStore((s) => s.sidePanelWidth);
  // Left-column tab: 0 = conversation list, 1 = activity feed. The header bell
  // swaps to the activity scene (an animated slide); the feed's close button
  // swaps back. Kept local (not a route) so the chat surface stays mounted.
  const [leftPanelTab, setLeftPanelTab] = useState(0);
  const handleCreateCompany = useCallback(() => {
    requestCreateOnNextMount();
    router.push('/business-directory');
  }, [router]);

  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  // Set from a `/u/<accountId>` invite deep link → NewChatModal auto-starts it.
  const [inviteAccountId, setInviteAccountId] = useState<string | null>(null);
  // Set from a `/g/<approver>/<groupId>` group invite deep link → after the 1-1
  // with the approver starts, NewChatModal sends them a group-join request.
  const [inviteGroupJoin, setInviteGroupJoin] = useState<{ groupId: string } | null>(null);
  const [isNewGroupOpen, setIsNewGroupOpen] = useState(false);
  const [gamePickerOpen, setGamePickerOpen] = useState(false);
  const groupCreateEnabled = isFeatureEnabled('FF_GROUP_CREATE');
  const { conversations } = useConversations();
  const { client } = useSession();

  // Belt-and-suspenders identity hydration driven by the RENDERED list (the iOS
  // approach). Whenever a conversation row is still a placeholder ("New chat" /
  // "Group chat") or has no avatar, resolve its member identities from the
  // directory — falling back to the SERVER account roster when the in-band
  // `ping.profile` broadcast never set a usable `peerAccountId`. This catches
  // rows synced via device-linking that the per-join / boot hydration missed
  // (wrong timing, or persisted by a path that didn't fire the callback). One
  // attempt per row per mount so a genuinely-nameless peer can't spin.
  const listHydrationAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    const pending = conversations.filter((c) => {
      const name = (c.name ?? '').trim();
      const placeholder = name.length === 0 || name === 'New chat' || name === 'Group chat';
      const noAvatar = c.avatarUri === undefined || c.avatarUri.length === 0;
      return (placeholder || noAvatar) && !listHydrationAttempted.current.has(c.id);
    });
    if (pending.length === 0) return;
    for (const c of pending) listHydrationAttempted.current.add(c.id);
    void (async () => {
      for (const c of pending) {
        try {
          await hydrateMemberProfiles(c.id);
        } catch {
          /* best-effort — never block the list */
        }
      }
    })();
  }, [conversations]);

  // Enforce the "ONE 1:1 per peer" invariant. Runs on EVERY conversation-list
  // change (not just growth): a duplicate can appear without the count growing
  // (a peerless invite-ghost resolving, a re-classify, a re-admit after login),
  // and the peer-keyed + name-keyed passes now also fold a peerless ghost into
  // its resolved-peer row. Idempotent and convergent — once collapsed there are
  // no duplicates so a re-run writes nothing. An in-flight guard prevents
  // overlapping passes from racing the same rows.
  const dedupeInFlightRef = useRef(false);
  useEffect(() => {
    if (dedupeInFlightRef.current) return;
    dedupeInFlightRef.current = true;
    void dedupeAllDirectConversations()
      .catch(() => undefined)
      .finally(() => {
        dedupeInFlightRef.current = false;
      });
  }, [conversations]);

  // Invite deep link: opened at `/u/<accountId>` (1-1) or `/g/<approver>/<groupId>`
  // (group). Open the new-chat modal to auto-start a 1-1 with the target/approver;
  // for a group link, also flag the join-request to send once that 1-1 exists.
  // Then clean the URL so a reload/back doesn't re-trigger it.
  useEffect(() => {
    const invite = takePendingInvite();
    if (invite === null) return;
    // Resolve the link HEADLESS — set only the auto-start props (no
    // `setIsNewChatOpen`), so NewChatModal runs the start-1-1 / group-join in the
    // background WITHOUT flashing the visible new-chat modal, then navigates.
    if (invite.kind === 'user') {
      setInviteAccountId(invite.accountId);
    } else {
      setInviteAccountId(invite.approverAccountId);
      setInviteGroupJoin({ groupId: invite.groupId });
    }
    if (typeof history !== 'undefined') {
      history.replaceState(null, '', '/chat');
    }
  }, []);

  // Right-utility panel layout (Threads/Notes/Tasks share one resizable
  // docked column). Side placement + width persist via the UI prefs
  // store; the active Notes/Tasks selection is toggled from the TopBar.
  const conversationsSide = useUiPreferencesStore((s) => s.conversationsSide);
  const threadsSide = useUiPreferencesStore((s) => s.threadsSide);
  const notesSide = useUiPreferencesStore((s) => s.notesSide);
  const tasksSide = useUiPreferencesStore((s) => s.tasksSide);
  // The full user profile now renders as a centered MODAL (ProfileOverlaysHost),
  // not a side utility panel — `profileSide` is no longer consulted here.
  const rightPanelWidth = useUiPreferencesStore((s) => s.rightPanelWidth);
  const setRightPanelWidth = useUiPreferencesStore((s) => s.setRightPanelWidth);
  const chatUtilityPanel = useUiPreferencesStore((s) => s.chatUtilityPanel);

  // URL is the source of truth for selection: `/chat/<id>` selects
  // the conversation whose peer account_id (direct) or local id
  // (group / fallback) matches. Deep links land directly on the
  // right chat; browser back/forward updates state via popstate.
  const [routeId, setRouteId] = useChatRouteId();

  // Resolve the route id to a local conversation id. Prefer
  // matching by peerAccountId (the user-visible URL key for
  // direct chats); fall back to matching by the local
  // conversation id so groups + legacy callers that push a raw
  // conv-id URL still resolve.
  const selectedConversationId = useMemo<string | null>(() => {
    if (routeId === null) return null;
    const byPeer = conversations.find(
      (c) => c.kind === 'direct' && c.peerAccountId === routeId,
    );
    if (byPeer !== undefined) return byPeer.id;
    const byId = conversations.find((c) => c.id === routeId);
    return byId?.id ?? null;
  }, [routeId, conversations]);
  const selected = conversations.find((c) => c.id === selectedConversationId);

  // Publish the open conversation so the notification service suppresses
  // banners for the chat the user is actively viewing (and clears its badge
  // contribution on open via the chat-room view effect).
  useEffect(() => {
    setActiveNotificationConversation(selectedConversationId);
    return () => setActiveNotificationConversation(null);
  }, [selectedConversationId]);

  // Live-query subscription handle for the conversation-game fold below.
  const threadGameSubRef = useRef<{ unsubscribe: () => void } | null>(null);

  // In-memory game-session projection for the active conversation.
  // `createGame` builds the `com.ping.game_create` body + a fresh
  // session id; `applyGameEvent` folds the optimistic local create in.
  // `sessions` + `makeMove` drive the INTERACTIVE thread game tiles —
  // thread replies belong to the same conversation, so this projection
  // is the authoritative session map the thread reads by sessionId.
  const { createGame, applyGameEvent, sessions: gameSessions, makeMove, replaceFromEvents } =
    useGameSessions(selectedConversationId ?? '');

  // Fold the conversation's PERSISTED game messages into the projection
  // so the thread tile renders the live phase/moves (and the outcome
  // survives reload), mirroring WebChatRoom's `persistedGameEvents`
  // effect. We subscribe to the full message list (not just the thread
  // replies) because a game's create + moves span the whole
  // conversation. `replaceFromEvents` is idempotent (pure fold from
  // scratch), so re-running on every change is safe.
  const [convGameRows, setConvGameRows] = useState<
    ReadonlyArray<{ body: string | undefined; isSelf: boolean; senderId: string; sentAt: number }>
  >([]);
  useEffect(() => {
    const convId = selectedConversationId;
    if (convId === null) {
      setConvGameRows([]);
      return;
    }
    let cancelled = false;
    void retrieveIdentity().then((identity) => {
      const selfId = identity?.accountId ?? '';
      const sub = liveQuery(() =>
        db.messages.where('conversationId').equals(convId).sortBy('sentAt'),
      ).subscribe({
        next: (rows) => {
          if (cancelled) return;
          setConvGameRows(
            rows.map((r) => ({
              body: r.body,
              isSelf: r.senderAccountId === selfId,
              senderId: r.senderAccountId,
              sentAt: r.sentAt,
            })),
          );
        },
        error: (err: unknown) => {
          log.warn({
            event: 'chat.thread.game.subscribe.failed',
            err: (err as Error).message,
          });
        },
      });
      if (cancelled) {
        sub.unsubscribe();
        return;
      }
      threadGameSubRef.current = sub;
    });
    return () => {
      cancelled = true;
      threadGameSubRef.current?.unsubscribe();
      threadGameSubRef.current = null;
    };
  }, [selectedConversationId]);
  const persistedThreadGameEvents = useMemo(
    () => gameEventsFromMessages(convGameRows),
    [convGameRows],
  );
  useEffect(() => {
    replaceFromEvents(persistedThreadGameEvents);
  }, [persistedThreadGameEvents, replaceFromEvents]);

  // Resolve the active conversation's MLS proxy and `conv.send` an
  // already-encoded envelope. Shared by the location / poll / game
  // overlays mounted at this route level (which don't own the
  // composer's `convProxyRef` cache in `WebChatRoom`).
  const sendEnvelopeToActiveConv = useCallback(
    async (envelopeBytes: Uint8Array): Promise<boolean> => {
      if (selectedConversationId === null) return false;
      const conv = await resolveActiveConv({
        client,
        conversationId: selectedConversationId,
        conversationName: selected?.name,
      });
      if (conv === null) return false;
      await conv.send(envelopeBytes);
      return true;
    },
    [client, selectedConversationId, selected?.name],
  );

  // ── Thread gateway (bug 4). Backs the shared `ThreadPanelOverlay`
  // mounted via `<ThreadPanelHost>`. The thread identifier is the root
  // message's contentHash. Replies are the Dexie rows whose envelope
  // `parent_id` equals that hash; `sendReply` `conv.send`s a
  // `com.ping.text` with `parent_id` = root (and, for the FIRST reply,
  // a `com.ping.thread_root` marker so receivers can anchor the thread)
  // then locally echoes the reply so the count card + panel update
  // without waiting on a server round-trip.
  const peerName = selected?.name ?? 'Chat';
  // Volatile display data the gateway needs (roster names/avatars, peer name,
  // conv name) — read through a REF so the gateway itself stays referentially
  // stable. `selected` is a fresh object on every `conversations` liveQuery
  // emit (a main-chat send bumps the conversation row), so closing over these
  // maps + keying the memo on them recreated the gateway on unrelated activity
  // → `useThreadViewModel` re-bootstrapped (a `loading` spinner + reload +
  // scroll-to-bottom in the open thread). The ref updates every render; the
  // memo below keys ONLY on the identity-defining `client` + conversation id.
  const threadGatewayCtxRef = useRef<{ conv: typeof selected; peerName: string }>({
    conv: selected,
    peerName,
  });
  threadGatewayCtxRef.current = { conv: selected, peerName };

  const threadGateway = useMemo<ThreadGateway>(() => {
    const convId = selectedConversationId;
    // Per-sender author resolution for GROUP threads — otherwise every
    // non-self reply collapses to the single conversation-level `peerName`
    // with no avatar (different senders look like the same person). Resolves
    // from the conversation roster (via the stable ref above); never surfaces
    // a raw account id.
    // TODO(threads): unify this inline gateway with `createWebThreadGateway`
    // (which also carries the call_id root fallback this one lacks).
    const authorOf = (
      rawSenderId: string | undefined,
      isSelf: boolean,
    ): { name: string; avatarUri: string | null } => {
      const { conv, peerName: fallbackName } = threadGatewayCtxRef.current;
      const raw = rawSenderId ?? '';
      // A peer's `senderAccountId` is a DEVICE HASH; the roster maps are keyed
      // by ACCOUNT id — resolve the hash first (no-op when `raw` already is an
      // account id, e.g. a self reply). This is why peer reply avatars showed
      // a bare initial instead of the picture.
      const accountId = conv?.memberDeviceAccounts?.[raw] ?? raw;
      // Avatar: roster (by account id, then raw) → for a 1:1 the conversation
      // picture IS the peer's avatar, so fall back to it (mirrors the activity
      // feed's `avatarFor`). Never use the 1:1 picture for a self author.
      let avatar =
        (accountId.length > 0 ? conv?.memberAvatarUris?.[accountId] : undefined) ??
        conv?.memberAvatarUris?.[raw];
      if (
        (avatar === undefined || avatar.length === 0) &&
        !isSelf &&
        conv?.kind === 'direct'
      ) {
        avatar = conv.avatarUri ?? conv.avatarMediaId;
      }
      const avatarUri = avatar !== undefined && avatar.length > 0 ? avatar : null;
      if (isSelf) {
        // Live self avatar (updates instantly on a Settings change), then our
        // own account's roster copy. So self replies (stacked mode) + a
        // self-rooted thread header show the real picture, not a 'You' initial.
        const selfStore = useSelfProfile.getState().avatarUrl;
        return {
          name: 'You',
          avatarUri: typeof selfStore === 'string' && selfStore.length > 0 ? selfStore : avatarUri,
        };
      }
      const name =
        (accountId.length > 0 ? conv?.memberDisplayNames?.[accountId] : undefined) ??
        conv?.memberDisplayNames?.[raw] ??
        conv?.memberDeviceNames?.[raw];
      return { name: name !== undefined && name.length > 0 ? name : fallbackName, avatarUri };
    };

    const buildSnapshot = async (
      identifier: ThreadIdentifier,
    ): Promise<ThreadOpenSnapshot> => {
      if (convId === null) {
        return emptyThreadSnapshot(identifier);
      }
      const rows = await db.messages
        .where('conversationId')
        .equals(convId)
        .toArray();
      const rootRow = rows.find((r) => r.contentHash === identifier);
      const identity = await retrieveIdentity();
      const selfId = identity?.accountId ?? '';

      const parentAuthor = authorOf(
        rootRow?.senderAccountId,
        rootRow !== undefined && rootRow.senderAccountId === selfId,
      );
      const parent: ThreadParentPreview = {
        messageId: identifier as unknown as ThreadParentPreview['messageId'],
        authorId: (rootRow?.senderAccountId ?? '') as unknown as ThreadParentPreview['authorId'],
        authorName: parentAuthor.name,
        authorImageUri: parentAuthor.avatarUri,
        previewText:
          rootRow?.body !== undefined ? lastMessagePreview(rootRow.body) ?? '' : '',
        timestamp: rootRow?.sentAt ?? Date.now(),
        // A redacted/deleted root disables the composer + shows "Original
        // message deleted" (ThreadOpenPanel reads `parent.deleted`). Was
        // hardcoded false → deleted root stayed repliable with stale preview.
        deleted: rootRow?.redactedAt !== undefined,
      };

      const rootRowId = rootRow?.id;
      const replies: ThreadReply[] = rows
        .filter((r) => {
          if (r.body === undefined) return false;
          const parsed = parseEnvelope(r.body);
          if (parsed === undefined) return false;
          // `com.ping.thread_root` is an anchor marker, not a reply — it
          // carries `parent_id` (so receivers can establish the thread) but
          // must never render as a reply row in the panel.
          if (parsed.kind === 'com.ping.thread_root') return false;
          // SENDER path: the local-echo body is the FULL envelope JSON, so
          // its `parent_id` equals the root's content hash (the identifier).
          if ('parent_id' in parsed && parsed.parent_id === identifier) {
            return true;
          }
          // RECEIVER path: incoming `com.ping.text` replies persist only
          // their plain text body (no wire `parent_id`), so we match on the
          // reconstructed local linkage instead — `inReplyTo.messageId`
          // points at the root's LOCAL row id (see useIncomingMessages).
          return (
            rootRowId !== undefined &&
            rootRowId.length > 0 &&
            r.inReplyTo?.messageId === rootRowId
          );
        })
        .sort((a, b) => a.sentAt - b.sentAt)
        .map((r) => {
          const isSelf = r.senderAccountId === selfId;
          const author = authorOf(r.senderAccountId, isSelf);
          // Project the row's REAL delivery state so a failed local echo
          // surfaces as a retry-able tile (not a phantom "sent"), and an
          // in-flight send shows the pending glyph. Peer replies leave
          // `deliveryState` unset → 'sent'.
          const status: ThreadReply['status'] =
            r.deliveryState === 'failed'
              ? 'failed'
              : r.deliveryState === 'pending'
                ? 'sending'
                : 'sent';
          // Read receipts on this reply (row stores `{accountId, readAt}`;
          // map to the read-side `{userId, timestamp}` shape). Patched on
          // at runtime by the shared `com.ping.read` handler, so read it
          // through a narrow cast.
          const rowReadBy = (
            r as { readBy?: ReadonlyArray<{ accountId: string; readAt: number }> }
          ).readBy;
          const readBy =
            rowReadBy !== undefined && rowReadBy.length > 0
              ? rowReadBy.map((x) => ({ userId: x.accountId, timestamp: x.readAt }))
              : undefined;
          return {
            id: r.id as unknown as ThreadReply['id'],
            threadId: identifier,
            authorId: r.senderAccountId as unknown as ThreadReply['authorId'],
            authorName: author.name,
            authorImageUri: author.avatarUri,
            body: r.body !== undefined ? lastMessagePreview(r.body) ?? '' : '',
            // Raw envelope so the thread row can render media tiles.
            ...(r.body !== undefined ? { rawBody: r.body } : {}),
            timestamp: r.sentAt,
            isSelf,
            status,
            ...(readBy !== undefined ? { readBy } : {}),
            ...(r.editedAt !== undefined ? { editedAt: r.editedAt } : {}),
          };
        });

      return { parent, replies, participants: [] };
    };

    return {
      load: (identifier) => buildSnapshot(identifier),
      subscribe: (identifier, callback) => {
        if (convId === null) return () => undefined;
        // Re-derive the snapshot whenever this conversation's message
        // table changes (Dexie liveQuery fires on every mutation).
        const sub = liveQuery(() =>
          db.messages.where('conversationId').equals(convId).count(),
        ).subscribe({
          next: () => {
            void buildSnapshot(identifier).then(callback);
          },
          error: (err: unknown) => {
            log.warn({
              event: 'chat.thread.subscribe.failed',
              err: (err as Error).message,
            });
          },
        });
        return () => sub.unsubscribe();
      },
      sendReply: async (identifier, body) => {
        const trimmed = body.trim();
        if (trimmed.length === 0) return;
        if (convId === null) {
          throw new Error('No conversation selected.');
        }
        const conv = await resolveActiveConv({
          client,
          conversationId: convId,
          conversationName: threadGatewayCtxRef.current.conv?.name,
        });
        if (conv === null) {
          throw new Error('Session not ready. Try again in a moment.');
        }

        // Is this the FIRST reply in the thread? If so emit a
        // `com.ping.thread_root` marker anchored on the root so peers
        // can establish the thread before the reply lands.
        const existing = await db.messages
          .where('conversationId')
          .equals(convId)
          .toArray();
        const hasPriorReply = existing.some((r) => {
          if (r.body === undefined) return false;
          const parsed = parseEnvelope(r.body);
          // The anchor marker is not a reply — ignore it here so the FIRST
          // real reply still sends even if a backfilled marker is present.
          return (
            parsed !== undefined &&
            parsed.kind !== 'com.ping.thread_root' &&
            'parent_id' in parsed &&
            parsed.parent_id === identifier
          );
        });
        const identity = await retrieveIdentity();
        if (!hasPriorReply) {
          await conv.send(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.thread_root',
              payload: { from: identity?.accountId ?? '' },
              parent_id: identifier,
            }),
          );
        }

        // SELF-IDENTIFYING thread reply: tag the reply itself with
        // `thread: true` so the read-side can fold it out of the main
        // timeline WITHOUT depending on the separate `thread_root` marker
        // arriving/persisting first. This is what makes the
        // ThreadPreviewCard appear reliably for BOTH sender and receiver
        // and keeps inline reply-quotes (no `thread` flag) in the timeline.
        const replyBytes = encodeEnvelopeJsonBytes({
          kind: 'com.ping.text',
          payload: { body: trimmed, thread: true },
          parent_id: identifier,
        });
        const sent = (await conv.send(replyBytes)) as {
          content_hash: Uint8Array;
          epoch: number | bigint;
        };

        // Local echo so the count card + panel reflect the reply.
        if (identity) {
          const contentHashHex = bytesToHex(sent.content_hash);
          const sentAt = Date.now();
          const replyBody = JSON.stringify({
            kind: 'com.ping.text',
            payload: { body: trimmed, thread: true },
            parent_id: identifier,
          });
          await sharedPersistSentMessage({
            messages: webMessagesPersister,
            conversations: webConversationsPersister,
            record: {
              id:
                typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                  ? crypto.randomUUID()
                  : `local-reply-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
              conversationId: convId,
              epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
              serverSeq: sentAt,
              contentHash: contentHashHex,
              senderAccountId: identity.accountId,
              body: replyBody,
              sentAt,
              deliveryState: 'sent',
            },
          });
        }
      },
      // Advance + broadcast this thread's read pointer so opening the
      // thread from the main chat clears its unread badge (and syncs to
      // the user's other devices). Was a no-op → the badge never cleared.
      markAsRead: async (identifier) => {
        await markThreadReadPointer(String(identifier));
      },
    };
    // Keyed ONLY on the identity-defining inputs. Roster names/avatars, peer
    // name, and conv name are read live via `threadGatewayCtxRef`, so an
    // unrelated `conversations` re-emit (e.g. a main-chat send) no longer
    // rebuilds the gateway and re-bootstraps the open thread.
  }, [client, selectedConversationId]);

  // Reaction aggregates for the active conversation, keyed by message id.
  // Thread replies look themselves up by id to render aggregated pills.
  const reactionsByMessageId = useReactions(
    selectedConversationId ?? '',
    webReactionsPersister,
  );

  // invite-flow chat-composer rewire — own the shared
  // `ChatRoomShellViewModel` at the route level and publish its
  // handlers via `ChatRoomShellHandlersProvider`. The shared
  // `ChatRoomComposerRich` (now mounted inside `<ChatRoom>`) reads
  // these handlers through `useChatRoomShellHandlers()` and dispatches
  // its Poll / Location menu entries into the same overlay stack the
  // `*Host` mounts below render from. Declared before the thread
  // media/poll effects below because they read `vm.state.threadIdentifier`.
  const vm = useChatRoomShellViewModel();

  // Live mirror of the open thread's root content hash. The thread
  // composer's send handlers + the thread game-move handler read this at
  // call-time so replies/moves post INTO the thread (parent_id = root).
  // Mutating a ref during render is safe for a mirror. Declared here
  // (early) so `handleThreadGameMove` below can read it.
  const threadIdRef = useRef<string | null>(null);
  threadIdRef.current = (vm.state.threadIdentifier as string | null) ?? null;

  // ── Thread media + poll functionality (parity with the main chat).
  // The thread panel renders replies through the SAME variant pipeline,
  // but the context-free `deriveVariantFromBody` can't decrypt S3 media
  // or project a live poll tally. We rebuild that host context here for
  // the OPEN thread's replies and feed it back via `buildVariant`.

  // Local account id — needed for poll vote attribution + "my vote".
  const [localAccountId, setLocalAccountId] = useState<string | null>(null);
  const localAccountIdRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void retrieveIdentity().then((id) => {
      if (cancelled || id === null) return;
      localAccountIdRef.current = id.accountId;
      setLocalAccountId(id.accountId);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The currently-open thread identifier (root content hash). Drives the
  // media/poll resolution below — when no thread is open these effects
  // stay idle.
  const openThreadId = (vm.state.threadIdentifier as string | null) ?? null;

  // Live mirror of the open thread's replies (id + rawBody), populated by
  // subscribing to the same gateway the panel reads. Used to drive image
  // decryption + the poll projection.
  const [threadReplies, setThreadReplies] = useState<
    ReadonlyArray<{ id: string; rawBody: string | undefined }>
  >([]);
  useEffect(() => {
    if (openThreadId === null) {
      setThreadReplies([]);
      return;
    }
    const unsub = threadGateway.subscribe(
      openThreadId as ThreadIdentifier,
      (snapshot) => {
        setThreadReplies(
          snapshot.replies.map((r) => ({
            id: r.id as unknown as string,
            rawBody: r.rawBody,
          })),
        );
      },
    );
    // Prime the initial snapshot (subscribe only fires on subsequent
    // mutations of the message table).
    void threadGateway
      .load(openThreadId as ThreadIdentifier)
      .then((snapshot) => {
        setThreadReplies(
          snapshot.replies.map((r) => ({
            id: r.id as unknown as string,
            rawBody: r.rawBody,
          })),
        );
      })
      .catch(() => undefined);
    return () => unsub();
  }, [openThreadId, threadGateway]);

  // reply id → poll content hash (the poll-vote `target`). The thread
  // reply carries only the local row id; the wire target is the row's
  // contentHash, looked up from Dexie. Built for poll replies only.
  const [pollTargetByReplyId, setPollTargetByReplyId] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const reply of threadReplies) {
        if (reply.rawBody === undefined) continue;
        if (envelopeKind(reply.rawBody) !== 'com.ping.poll') continue;
        const row = await db.messages.get(reply.id);
        const hash = row?.contentHash;
        if (hash !== undefined && hash.length > 0) next[reply.id] = hash;
      }
      if (!cancelled) setPollTargetByReplyId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [threadReplies]);

  // Personal-bookmark state for the OPEN thread's parent conversation, so the
  // thread action menu reflects pinned / saved / starred state (Unpin / Unsave
  // / Unstar). `hashByReplyId` maps each reply → its content hash; the three
  // sets are the committed pinned/saved/starred hashes (shared with the room).
  const [threadHashByReplyId, setThreadHashByReplyId] = useState<Record<string, string>>({});
  const [threadPinnedHashes, setThreadPinnedHashes] = useState<ReadonlySet<string>>(new Set());
  const [threadSavedHashes, setThreadSavedHashes] = useState<ReadonlySet<string>>(new Set());
  const [threadStarredHashes, setThreadStarredHashes] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const reply of threadReplies) {
        const row = await db.messages.get(reply.id);
        const hash = row?.contentHash;
        if (hash !== undefined && hash.length > 0) next[reply.id] = hash;
      }
      if (!cancelled) setThreadHashByReplyId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [threadReplies]);
  useEffect(() => {
    const cid = selectedConversationId;
    if (cid === null) {
      setThreadPinnedHashes(new Set());
      setThreadSavedHashes(new Set());
      return undefined;
    }
    const unsubPin = webPinnedPersister.subscribe?.(cid, (t) => setThreadPinnedHashes(new Set(t)));
    const unsubSave = webSavedPersister.subscribe?.(cid, (t) => setThreadSavedHashes(new Set(t)));
    return () => {
      unsubPin?.();
      unsubSave?.();
    };
  }, [selectedConversationId]);
  useEffect(
    () => webStarredPersister.subscribe?.((t) => setThreadStarredHashes(new Set(t))),
    [],
  );

  // Decrypted image preview object URLs for thread image replies, keyed
  // by reply id. Mirrors WebChatRoom's `imagePreviewByMessageId` effect:
  // fetch + decrypt the encrypted S3 blob → object URL, skipping pending
  // placeholders and revoking URLs on cleanup.
  const [threadImagePreviewByReplyId, setThreadImagePreviewByReplyId] =
    useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    const minted: string[] = [];
    for (const reply of threadReplies) {
      if (reply.rawBody === undefined) continue;
      const payload = parseImageEnvelope(reply.rawBody);
      if (payload === undefined) continue;
      // Skip pending local-echo placeholders (sentinel blob_ref).
      if (payload.blob_ref.media_id === 'pending') continue;
      void fetchAndDecryptMediaBlob(payload.blob_ref)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          minted.push(url);
          setThreadImagePreviewByReplyId((prev) => {
            if (prev[reply.id] !== undefined) {
              URL.revokeObjectURL(url);
              return prev;
            }
            return { ...prev, [reply.id]: url };
          });
        })
        .catch((err) => {
          log.warn({
            event: 'chat.thread.image.fetch.failed',
            err: (err as Error).message,
          });
        });
    }
    return () => {
      cancelled = true;
      for (const url of minted) URL.revokeObjectURL(url);
    };
  }, [threadReplies]);

  // Decrypted poster + video object URLs for thread VIDEO replies, keyed
  // by reply id. Mirrors WebChatRoom's video resolution: fetch + decrypt
  // the encrypted S3 poster (eager) and the full video blob so the inline
  // <VideoAttachmentCard> player is fully functional in the thread (parity
  // with the main chat, which otherwise only had a non-viewable envelope
  // url here → a blank/broken tile). Object URLs are revoked on cleanup.
  const [threadVideoByReplyId, setThreadVideoByReplyId] = useState<
    Record<string, { videoUri?: string; posterUri?: string }>
  >({});
  useEffect(() => {
    let cancelled = false;
    const minted: string[] = [];
    for (const reply of threadReplies) {
      if (reply.rawBody === undefined) continue;
      const payload = parseVideoEnvelope(reply.rawBody);
      if (payload === undefined) continue;
      // Skip pending local-echo placeholders (sentinel blob_ref).
      if (payload.blob_ref.media_id === 'pending') continue;
      const setUri = (key: 'videoUri' | 'posterUri', url: string): void => {
        setThreadVideoByReplyId((prev) => {
          const existing = prev[reply.id];
          if (existing?.[key] !== undefined) {
            URL.revokeObjectURL(url);
            return prev;
          }
          return { ...prev, [reply.id]: { ...existing, [key]: url } };
        });
      };
      if (payload.poster_ref !== undefined) {
        void fetchAndDecryptMediaBlob(payload.poster_ref)
          .then((blob) => {
            if (cancelled) return;
            const url = URL.createObjectURL(blob);
            minted.push(url);
            setUri('posterUri', url);
          })
          .catch((err) => {
            log.warn({
              event: 'chat.thread.video.poster.fetch.failed',
              err: (err as Error).message,
            });
          });
      }
      void fetchAndDecryptMediaBlob(payload.blob_ref)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          minted.push(url);
          setUri('videoUri', url);
        })
        .catch((err) => {
          log.warn({
            event: 'chat.thread.video.fetch.failed',
            err: (err as Error).message,
          });
        });
    }
    return () => {
      cancelled = true;
      for (const url of minted) URL.revokeObjectURL(url);
    };
  }, [threadReplies]);

  // Live poll-vote projection for thread poll replies, keyed by poll
  // target hash. Mirrors WebChatRoom's `pollVotesByTarget` effect.
  const [threadPollVotesByTarget, setThreadPollVotesByTarget] = useState<
    Record<
      string,
      {
        counts: Record<number, number>;
        myVote?: number;
        // Full multi-select set the local account holds (all option indices).
        myVotes?: number[];
        total: number;
        votersByOption: Record<number, string[]>;
      }
    >
  >({});
  useEffect(() => {
    const targets = Object.values(pollTargetByReplyId).filter(
      (h): h is string => !!h && h.length > 0,
    );
    if (targets.length === 0) return;
    const me = localAccountId ?? undefined;
    const apply = (
      target: string,
      rows: ReadonlyArray<{ actorAccountId: string; optionIndex: number }>,
    ): void => {
      const counts: Record<number, number> = {};
      const votersByOption: Record<number, string[]> = {};
      const myVotes: number[] = [];
      const actors = new Set<string>();
      for (const r of rows) {
        counts[r.optionIndex] = (counts[r.optionIndex] ?? 0) + 1;
        (votersByOption[r.optionIndex] ??= []).push(r.actorAccountId);
        actors.add(r.actorAccountId);
        if (me !== undefined && r.actorAccountId === me) myVotes.push(r.optionIndex);
      }
      // `myVote` (single, back-compat) = first of the local account's rows;
      // `myVotes` = the full multi-select set.
      const myVote = myVotes.length > 0 ? myVotes[0] : undefined;
      setThreadPollVotesByTarget((prev) => ({
        ...prev,
        [target]: {
          counts,
          total: actors.size,
          votersByOption,
          ...(myVote !== undefined ? { myVote } : {}),
          ...(myVotes.length > 0 ? { myVotes } : {}),
        },
      }));
    };
    const unsubs = targets.map((target) => {
      void webPollVotesPersister.listByPoll(target).then((rows) => apply(target, rows));
      return (
        webPollVotesPersister.subscribe?.(target, (rows) => apply(target, rows)) ??
        (() => undefined)
      );
    });
    return () => unsubs.forEach((u) => u());
  }, [pollTargetByReplyId, localAccountId]);

  // Live mirrors so `voteThreadPoll` can read the CURRENT projection +
  // reply bodies (to resolve multi_select + toggle the actor's set)
  // without re-creating the callback on every tick.
  const threadPollVotesByTargetRef = useRef(threadPollVotesByTarget);
  useEffect(() => {
    threadPollVotesByTargetRef.current = threadPollVotesByTarget;
  }, [threadPollVotesByTarget]);
  const pollTargetByReplyIdRef = useRef(pollTargetByReplyId);
  useEffect(() => {
    pollTargetByReplyIdRef.current = pollTargetByReplyId;
  }, [pollTargetByReplyId]);
  const threadRepliesRef = useRef(threadReplies);
  useEffect(() => {
    threadRepliesRef.current = threadReplies;
  }, [threadReplies]);

  // Cast a thread poll vote: optimistic local OR-set replace, then emit a
  // `com.ping.poll_vote` envelope into the active conversation. Mirrors
  // WebChatRoom's `handlePollVote` (single = replace with `[optionIndex]`,
  // multi = toggle membership of the actor's set).
  const voteThreadPoll = useCallback(
    (pollTarget: string, optionIndex: number): void => {
      const me = localAccountIdRef.current ?? '__local__';
      const now = Date.now();
      // Resolve multi_select from the poll reply's create body.
      const replyId = Object.keys(pollTargetByReplyIdRef.current).find(
        (id) => pollTargetByReplyIdRef.current[id] === pollTarget,
      );
      const rawBody =
        replyId !== undefined
          ? threadRepliesRef.current.find((r) => r.id === replyId)?.rawBody
          : undefined;
      let multiSelect = false;
      if (rawBody !== undefined) {
        const parsed = parseEnvelope(rawBody);
        if (parsed?.kind === 'com.ping.poll') {
          multiSelect = (parsed.body as { multi_select?: boolean }).multi_select === true;
        }
      }

      // Compute the actor's NEXT full option set.
      let nextSet: number[];
      if (multiSelect) {
        const current = threadPollVotesByTargetRef.current[pollTarget]?.myVotes ?? [];
        nextSet = current.includes(optionIndex)
          ? current.filter((i) => i !== optionIndex)
          : [...current, optionIndex];
      } else {
        nextSet = [optionIndex];
      }

      // Optimistic local OR-set replace (atomic delete-then-insert).
      if (webPollVotesPersister.replaceActorVotes !== undefined) {
        void webPollVotesPersister.replaceActorVotes(pollTarget, me, nextSet, now);
      } else {
        void (async () => {
          await webPollVotesPersister.deleteByActor(pollTarget, me);
          for (const idx of nextSet) {
            await webPollVotesPersister.put({
              id: `${pollTarget}:${me}:${idx}`,
              pollTarget,
              actorAccountId: me,
              optionIndex: idx,
              votedAt: now,
            });
          }
        })();
      }

      void (async () => {
        try {
          await sendEnvelopeToActiveConv(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.poll_vote',
              payload: multiSelect
                ? { target: pollTarget, choices: nextSet, actorAccountId: me }
                : {
                    target: pollTarget,
                    choices: nextSet,
                    option_index: optionIndex,
                    actorAccountId: me,
                  },
            }),
          );
        } catch (err) {
          log.warn({ event: 'chat.thread.poll.vote.failed', err: (err as Error).message });
        }
      })();
    },
    [sendEnvelopeToActiveConv],
  );

  // Fold the conversation's meeting stream (create + rsvp/update/cancel — all
  // FULL_ENVELOPE_BODY_KINDS message rows) into live state, so a meeting posted
  // INSIDE A THREAD renders an INTERACTIVE card there (live counts + working
  // RSVP), not a frozen display-only tile. Mirrors WebChatRoom's
  // `persistedMeetingState`. `convGameRows` already carries every conversation
  // row (body / isSelf / senderId=account / sentAt); self rows attribute to our
  // account (creator gate + RSVP highlight), peer rows to the sender's account.
  const threadMeetingState = useMemo<MeetingState>(() => {
    const wireToKind = (kind: string): MeetingEvent['kind'] | undefined =>
      kind === 'com.ping.meeting'
        ? 'create'
        : kind === 'com.ping.meeting_update'
          ? 'update'
          : kind === 'com.ping.meeting_rsvp'
            ? 'rsvp'
            : kind === 'com.ping.meeting_cancel'
              ? 'cancel'
              : undefined;
    const rows: Array<{
      kind: MeetingEvent['kind'];
      body: unknown;
      senderAccountId: string;
      at: number;
    }> = [];
    // Resolve peer device hex → ACCOUNT id from the roster, so voters/creator
    // key by account (the responses list maps them to a name + avatar, and the
    // creator gate matches). Our own self-echo rows already carry our account.
    const deviceAccounts = selected?.memberDeviceAccounts;
    for (const r of convGameRows) {
      if (r.body === undefined || r.body.length === 0 || r.body.charAt(0) !== '{') continue;
      const parsed = parseEnvelope(r.body);
      if (parsed === undefined || !('body' in parsed)) continue;
      const kind = wireToKind(parsed.kind);
      if (kind === undefined) continue;
      rows.push({
        kind,
        body: (parsed as { body: unknown }).body,
        senderAccountId: deviceAccounts?.[r.senderId] ?? r.senderId,
        at: r.sentAt,
      });
    }
    rows.sort((a, b) => a.at - b.at);
    let state = EMPTY_MEETING_STATE;
    for (const r of rows) {
      state = reduceMeeting(
        state,
        { kind: r.kind, body: r.body } as unknown as MeetingEvent,
        { senderAccountId: r.senderAccountId, at: r.at },
      );
    }
    return state;
  }, [convGameRows, selected?.memberDeviceAccounts]);

  // Send a meeting sidecar (rsvp / update / cancel) from a THREAD card + a local
  // echo (self-sends don't round-trip). Sidecars carry no `parent_id` — they're
  // keyed by `meeting_id` and fold EVERYWHERE, so the card updates in the thread
  // AND the main chat. This is the exact same wire path as WebChatRoom's
  // `sendMeetingSidecar`, so the two surfaces stay behaviourally identical.
  const sendThreadMeetingSidecar = useCallback(
    async (
      kind: 'update' | 'rsvp' | 'cancel',
      meetingId: string,
      payload: Record<string, unknown>,
    ): Promise<void> => {
      const wireKind: 'com.ping.meeting_update' | 'com.ping.meeting_rsvp' | 'com.ping.meeting_cancel' =
        kind === 'update'
          ? 'com.ping.meeting_update'
          : kind === 'rsvp'
            ? 'com.ping.meeting_rsvp'
            : 'com.ping.meeting_cancel';
      const envelopeBody = { kind: wireKind, payload };
      try {
        await sendEnvelopeToActiveConv(encodeEnvelopeJsonBytes(envelopeBody));
      } catch (err) {
        log.warn({ event: 'chat.thread.meeting.sidecar.failed', err: (err as Error).message });
      }
      const identity = await retrieveIdentity();
      const convId = selectedConversationId;
      if (identity && convId !== null) {
        const sentAt = Date.now();
        await sharedPersistSentMessage({
          messages: webMessagesPersister,
          conversations: webConversationsPersister,
          record: {
            id:
              typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `local-meeting-${kind}-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
            conversationId: convId,
            epoch: 0,
            serverSeq: sentAt,
            contentHash: `local-meeting-${kind}-${meetingId}-${sentAt}`,
            senderAccountId: identity.accountId,
            body: JSON.stringify(envelopeBody),
            sentAt,
            deliveryState: 'sent',
          },
        });
      }
    },
    [sendEnvelopeToActiveConv, selectedConversationId],
  );
  const handleThreadMeetingRsvp = useCallback(
    (meetingId: string, response: MeetingRsvpResponse): void => {
      void sendThreadMeetingSidecar('rsvp', meetingId, buildMeetingRsvpPayload(meetingId, response));
    },
    [sendThreadMeetingSidecar],
  );
  const handleThreadMeetingCancel = useCallback(
    (meetingId: string): void => {
      void sendThreadMeetingSidecar('cancel', meetingId, buildMeetingCancelPayload(meetingId));
      void webMeetingRemindersPersister.remove(meetingId).catch(() => undefined);
    },
    [sendThreadMeetingSidecar],
  );
  // Creator edit: open the shared MeetingModal pre-filled, then send an update diff.
  const [editingThreadMeeting, setEditingThreadMeeting] = useState<{
    meetingId: string;
    initial: MeetingModalInitial;
  } | null>(null);
  const handleThreadMeetingEdit = useCallback(
    (meetingId: string): void => {
      const rec = threadMeetingState.meetings[meetingId];
      if (rec === undefined) return;
      setEditingThreadMeeting({
        meetingId,
        initial: {
          kind: rec.autoCall !== undefined ? 'call' : 'meeting',
          title: rec.title,
          ...(rec.description !== undefined ? { description: rec.description } : {}),
          startsAt: rec.startsAt,
          endsAt: rec.endsAt,
          ...(rec.location !== undefined ? { location: rec.location } : {}),
          ...(rec.meetingLink !== undefined ? { meetingLink: rec.meetingLink } : {}),
          ...(rec.autoCall !== undefined ? { autoCallMedia: rec.autoCall.media } : {}),
        },
      });
    },
    [threadMeetingState],
  );
  const submitThreadMeetingEdit = useCallback(
    (meetingId: string, draft: MeetingDraft): void => {
      void sendThreadMeetingSidecar(
        'update',
        meetingId,
        buildMeetingUpdatePayload({
          meetingId,
          title: draft.title,
          startsAt: draft.startsAt,
          endsAt: draft.endsAt,
          location: draft.location ?? '',
          meetingLink: draft.meetingLink ?? '',
          description: draft.description ?? '',
        }),
      );
    },
    [sendThreadMeetingSidecar],
  );
  // Responses sheet — same account-keyed name/avatar resolution as the main chat.
  const [responsesThreadMeetingId, setResponsesThreadMeetingId] = useState<string | null>(null);
  const threadMeetingResponses = useMemo(() => {
    const groups: {
      yes: Array<{ accountId: string; name: string; avatarUri?: string }>;
      maybe: Array<{ accountId: string; name: string; avatarUri?: string }>;
      no: Array<{ accountId: string; name: string; avatarUri?: string }>;
    } = { yes: [], maybe: [], no: [] };
    if (responsesThreadMeetingId === null) return groups;
    const byVoter = threadMeetingState.rsvps[responsesThreadMeetingId] ?? {};
    const names = selected?.memberDisplayNames ?? {};
    const avatars = selected?.memberAvatarUris ?? {};
    for (const rsvp of Object.values(byVoter)) {
      const acct = rsvp.voterAccountId;
      const name = acct === localAccountId ? 'You' : (names[acct] ?? 'Member');
      const avatarUri = avatars[acct];
      groups[rsvp.response].push({
        accountId: acct,
        name,
        ...(avatarUri !== undefined ? { avatarUri } : {}),
      });
    }
    return groups;
  }, [responsesThreadMeetingId, threadMeetingState, localAccountId, selected?.memberDisplayNames, selected?.memberAvatarUris]);
  const responsesThreadTitle =
    responsesThreadMeetingId !== null
      ? (threadMeetingState.meetings[responsesThreadMeetingId]?.title ?? 'Meeting')
      : '';
  const handleThreadJoinMeetingCall = useCallback(
    (media: 'audio' | 'video'): void => {
      const convId = selectedConversationId;
      if (convId === null) return;
      void useCallStore.getState().startCall({
        conversationId: convId,
        media,
        title: selected?.name ?? 'Call',
        isGroup: selected?.kind === 'group',
        peerAccountIds: Object.keys(selected?.memberDisplayNames ?? {}).filter(
          (id) => id !== localAccountId,
        ),
        peerDisplayNames: selected?.memberDisplayNames ?? {},
      });
    },
    [selectedConversationId, selected?.name, selected?.kind, selected?.memberDisplayNames, localAccountId],
  );
  const handleThreadOpenMeetingLink = useCallback((url: string): void => {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      /* best-effort */
    }
  }, []);
  const handleThreadCopyMeetingLink = useCallback((url: string): void => {
    try {
      void navigator.clipboard?.writeText(url);
    } catch {
      /* best-effort */
    }
  }, []);

  // Emit a game move from a THREAD tile. Mirrors WebChatRoom's
  // `handleGameMove` but stamps `parent_id` = the open thread root so
  // the move nests under the thread (and the thread tile updates). The
  // move also lands in the conversation, so the main-chat tile + the
  // peer converge on the same session state. Optimistic fold + local
  // echo persist keep the move visible before the round-trip / after a
  // reload (self-sends don't echo via onMessage).
  const handleThreadGameMove = useCallback(
    (sessionId: string, moveIdx: number, payload: string): void => {
      const convId = selectedConversationId;
      if (convId === null) return;
      const threadTarget = threadIdRef.current;
      void (async () => {
        try {
          const conv = await resolveActiveConv({
            client,
            conversationId: convId,
            conversationName: selected?.name,
          });
          if (conv === null) return;
          const body = makeMove(sessionId, { moveIdx, payload });
          const envelopeBody = {
            kind: 'com.ping.game_move' as const,
            payload: { ...body },
            ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
          };
          const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
            content_hash: Uint8Array;
            epoch: number | bigint;
          };
          applyGameEvent({ type: 'move', sessionId, moveIdx, payload, by: '__self__' });
          const identity = await retrieveIdentity();
          if (identity) {
            const sentAt = Date.now();
            await sharedPersistSentMessage({
              messages: webMessagesPersister,
              conversations: webConversationsPersister,
              record: {
                id:
                  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `local-threadgamemove-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                conversationId: convId,
                epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                serverSeq: sentAt,
                contentHash: bytesToHex(sent.content_hash),
                senderAccountId: identity.accountId,
                body: JSON.stringify(envelopeBody),
                sentAt,
                deliveryState: 'sent',
                ...(threadTarget !== null
                  ? { inReplyTo: { messageId: threadTarget, preview: 'Game move' } }
                  : {}),
              },
            });
          }
        } catch (err) {
          log.warn({ event: 'chat.thread.game.move.failed', err: (err as Error).message });
        }
      })();
    },
    [client, selectedConversationId, selected?.name, makeMove, applyGameEvent],
  );

  // Fullscreen lightbox for thread image replies (reuses the same
  // <ImageLightbox> the main chat uses). The thread image variant's
  // `onTapOpen` sets the resolved object URL here.
  const [threadLightboxUri, setThreadLightboxUri] = useState<string | null>(null);
  // "View votes" panel for a poll posted as a thread reply. Voter names are
  // resolved best-effort here (self → "You", others → "Member") since the
  // thread host has no group roster; refined alongside thread author
  // resolution. The tally (counts/total) + anonymous flag are authoritative.
  const [threadVotesPanel, setThreadVotesPanel] = useState<{
    poll: PollViewContent;
    counts: Record<number, number>;
    total: number;
    votersByOption?: Record<number, ReadonlyArray<PollVoterAvatar>>;
  } | null>(null);

  // Forward flow for a THREAD reply — mirrors WebChatRoom's forward
  // (open a conversation picker, then re-send the reply's content as a
  // `com.ping.forward` envelope into the chosen conversation). Wired into
  // `threadRowActions.onMenuAction('forward', …)` below so the Forward
  // entry works on a thread reply, not just a main-chat bubble.
  const [threadForwardSource, setThreadForwardSource] = useState<{
    body: string;
    contentHash: string;
    senderId: string;
  } | null>(null);
  const threadForwardTargets = useMemo(
    () =>
      conversations
        .filter((c) => c.id !== selectedConversationId)
        .map((c) => ({ id: c.id, name: c.name ?? 'Chat' })),
    [conversations, selectedConversationId],
  );
  const handleThreadForwardPick = useCallback(
    (targetConversationId: string): void => {
      const source = threadForwardSource;
      setThreadForwardSource(null);
      if (source === null || client === null) return;
      const target = conversations.find((c) => c.id === targetConversationId);
      void (async () => {
        try {
          const transport = getCurrentTransport();
          const result = await getOrCreateMlsConversation({
            client: client as unknown as Parameters<
              typeof getOrCreateMlsConversation
            >[0]['client'],
            transport,
            beConversationId: targetConversationId,
            conversationName: target?.name,
            log,
          });
          // Text forwards carry the body verbatim; rich content falls back
          // to its preview text (per-target media re-encryption is a
          // follow-up — identical to the main chat's forward behaviour).
          const parsed = parseEnvelope(source.body);
          let innerPayload: Record<string, unknown>;
          if (parsed && parsed.kind === 'com.ping.text') {
            innerPayload = parsed.body as Record<string, unknown>;
          } else {
            const preview = lastMessagePreview(source.body) ?? '';
            if (preview.length === 0) return;
            innerPayload = { body: preview };
          }
          const forwardEnvelope = {
            kind: 'com.ping.forward' as const,
            payload: {
              source_event_id: source.contentHash.length > 0 ? source.contentHash : '',
              source_conversation_id: selectedConversationId ?? '',
              content_type: appEventTypeForKind('com.ping.text') ?? 0x11,
              content_body: innerPayload,
              ...(source.senderId.length > 0 ? { original_sender: source.senderId } : {}),
            },
          };
          const sent = (await result.conv.send(
            encodeEnvelopeJsonBytes(forwardEnvelope),
          )) as { content_hash: Uint8Array; epoch: number | bigint };
          // Local echo into the TARGET conversation so the forwarder sees
          // their own forwarded message (self-sends don't echo via onMessage).
          const identity = await retrieveIdentity();
          if (identity !== null) {
            const sentAt = Date.now();
            await sharedPersistSentMessage({
              messages: webMessagesPersister,
              conversations: webConversationsPersister,
              record: {
                id:
                  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `local-forward-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                conversationId: targetConversationId,
                epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                serverSeq: sentAt,
                contentHash: bytesToHex(sent.content_hash),
                senderAccountId: identity.accountId,
                body: JSON.stringify(forwardEnvelope),
                sentAt,
                deliveryState: 'sent',
              },
            });
          }
          pingToast.success({ title: 'Message forwarded' });
        } catch (err) {
          log.warn({ event: 'chat.thread.forward.failed', err: (err as Error).message });
        }
      })();
    },
    [threadForwardSource, client, conversations, selectedConversationId],
  );

  // Open a THREAD doc reply — fetch + decrypt the encrypted blob, then
  // open it in a new tab. Mirrors the main chat's `handleDocTap` (minus
  // the in-app PDF reader, which the thread panel doesn't host).
  const handleThreadDocTap = useCallback((rawBody: string): void => {
    const payload = parseFileEnvelope(rawBody);
    if (payload === undefined || payload.blob_ref.media_id === 'pending') return;
    void (async () => {
      try {
        const blob = await fetchAndDecryptMediaBlob(payload.blob_ref);
        const typed =
          payload.mime.length > 0 ? new Blob([blob], { type: payload.mime }) : blob;
        const url = URL.createObjectURL(typed);
        window.open(url, '_blank', 'noopener,noreferrer');
        // Revoke after a grace period so the opened tab can load it first.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err) {
        log.warn({ event: 'chat.thread.doc.open.failed', err: (err as Error).message });
      }
    })();
  }, []);

  // ── Read receipts on thread replies (parity with the main chat). A self
  // reply flips to the ✓✓ (read) glyph once `readBy` reaches the group's
  // non-self member count (or the first peer read in a 1:1). Reader
  // names/avatars resolve through the same device_hash → account_id maps
  // the main chat uses (readBy[].userId is a device hash).
  const threadIsGroup = selected?.kind === 'group';
  const threadGroupNonSelfCount = threadIsGroup ? selected?.memberAccountIds?.length ?? 0 : 0;
  const resolveThreadReaderName = useCallback(
    (userId: string): string | undefined => {
      const accountId = selected?.memberDeviceAccounts?.[userId];
      return (
        selected?.memberDisplayNames?.[userId] ??
        (accountId !== undefined ? selected?.memberDisplayNames?.[accountId] : undefined) ??
        selected?.memberDeviceNames?.[userId]
      );
    },
    [selected?.memberDeviceAccounts, selected?.memberDisplayNames, selected?.memberDeviceNames],
  );
  const resolveThreadReaderAvatar = useCallback(
    (userId: string): string | undefined => {
      const accountId = selected?.memberDeviceAccounts?.[userId];
      const avatars = selected?.memberAvatarUris;
      return avatars?.[userId] ?? (accountId !== undefined ? avatars?.[accountId] : undefined);
    },
    [selected?.memberDeviceAccounts, selected?.memberAvatarUris],
  );
  // Own avatar for the self poll voter. The GROUP roster (`memberAvatarUris`)
  // rarely carries the local account, and a 1:1 never does, so fall back to the
  // self-profile store — otherwise "You" rendered as a gray initial.
  const selfProfileAvatarUrl = useSelfProfile((s) => s.avatarUrl);
  // Resolve a poll voter account id → { initials, avatarUrl?, name } for the
  // poll tile's per-option avatar stack + the "Poll votes" panel. Mirrors the
  // main chat's `resolvePollVoter`: self → "You" + our own avatar; a GROUP member
  // via the roster resolvers; and — the case the thread previously got wrong — a
  // 1:1 PEER via the conversation name + avatar (the group roster maps are empty
  // for a DM, so the peer used to render as a nameless gray "Member").
  const resolveThreadPollVoter = useCallback(
    (id: string): PollVoterAvatar => {
      const isSelf = id === localAccountId || id === '__local__';
      if (isSelf) {
        const selfAvatar =
          (localAccountId !== null ? resolveThreadReaderAvatar(localAccountId) : undefined) ??
          (selfProfileAvatarUrl !== null && selfProfileAvatarUrl.length > 0
            ? selfProfileAvatarUrl
            : undefined);
        return selfAvatar !== undefined && selfAvatar.length > 0
          ? { initials: 'Y', avatarUrl: selfAvatar, name: 'You' }
          : { initials: 'Y', name: 'You' };
      }
      const peerFallbackName = !threadIsGroup ? selected?.name : undefined;
      const peerFallbackAvatar = !threadIsGroup
        ? selected?.avatarUri ??
          (selected?.peerAccountId !== undefined
            ? selected?.memberAvatarUris?.[selected.peerAccountId]
            : undefined)
        : undefined;
      const name = resolveThreadReaderName(id) ?? peerFallbackName ?? 'Member';
      const avatarUrl = resolveThreadReaderAvatar(id) ?? peerFallbackAvatar;
      const trimmed = name.trim();
      const initials = trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?';
      return avatarUrl !== undefined && avatarUrl.length > 0
        ? { initials, avatarUrl, name }
        : { initials, name };
    },
    [
      localAccountId,
      resolveThreadReaderName,
      resolveThreadReaderAvatar,
      selfProfileAvatarUrl,
      threadIsGroup,
      selected?.name,
      selected?.avatarUri,
      selected?.peerAccountId,
      selected?.memberAvatarUris,
    ],
  );
  const resolveThreadVotersByOption = useCallback(
    (
      raw: Record<number, ReadonlyArray<string>>,
    ): Record<number, ReadonlyArray<PollVoterAvatar>> => {
      const out: Record<number, ReadonlyArray<PollVoterAvatar>> = {};
      for (const [idx, ids] of Object.entries(raw)) {
        out[Number(idx)] = ids.map(resolveThreadPollVoter);
      }
      return out;
    },
    [resolveThreadPollVoter],
  );
  // Canonicalise a thread reply's `authorId` / mention id → the ACCOUNT id the
  // profile endpoint needs, plus a name/avatar hint. A peer reply's `authorId`
  // is a DEVICE HASH (not an account id), and a 1:1 sender id isn't the peer's
  // account id — passing either straight to `openMiniProfile` made
  // `GET /v1/users/{id}/profile` 400 → "Profile unavailable". Mirrors
  // WebChatRoom's `resolveProfileTarget`.
  const resolveThreadProfileTarget = useCallback(
    (raw: string): { accountId: string; hint: { displayName?: string; avatarUri?: string } } | null => {
      if (raw.length === 0) return null;
      const conv = selected;
      if (conv === undefined) return null;
      // 1:1 → the only "other" person is the peer; use their base64url account id.
      if (conv.kind !== 'group') {
        const peerId = conv.peerAccountId ?? raw;
        const peerAvatar = conv.memberAvatarUris?.[peerId] ?? conv.memberAvatarUris?.[raw];
        return {
          accountId: peerId,
          hint: {
            ...(conv.name !== undefined && conv.name.length > 0 ? { displayName: conv.name } : {}),
            ...(peerAvatar !== undefined ? { avatarUri: peerAvatar } : {}),
          },
        };
      }
      // Group → device hash → account id via the roster (passthrough when `raw`
      // already is an account id, e.g. a self reply or a group @mention).
      const accountId = conv.memberDeviceAccounts?.[raw] ?? raw;
      const displayName =
        conv.memberDisplayNames?.[accountId] ??
        conv.memberDisplayNames?.[raw] ??
        conv.memberDeviceNames?.[raw];
      const avatarUri = conv.memberAvatarUris?.[accountId] ?? conv.memberAvatarUris?.[raw];
      return {
        accountId,
        hint: {
          ...(displayName !== undefined && displayName.length > 0 ? { displayName } : {}),
          ...(avatarUri !== undefined ? { avatarUri } : {}),
        },
      };
    },
    [selected],
  );

  // ── Edit a thread reply (parity with the main chat). The hover pencil on
  // a self reply sets the composer into edit mode; Send routes to
  // `submitEditThreadReply` (a `com.ping.edit` envelope + an optimistic
  // local rewrite that PRESERVES the reply's `com.ping.text` + `parent_id`
  // envelope, so the edited reply stays anchored in the thread rather than
  // dropping out as a bare parent-less text row).
  const [editingThreadReply, setEditingThreadReply] = useState<{
    id: string;
    body: string;
    contentHash: string;
  } | null>(null);
  const editingThreadMessage = useMemo<Message | null>(() => {
    if (editingThreadReply === null) return null;
    return {
      id: editingThreadReply.id,
      conversationId: selectedConversationId ?? '',
      senderId: localAccountId ?? '',
      body: editingThreadReply.body,
      hlc: '',
      contentHash: editingThreadReply.contentHash,
      timestamp: Date.now(),
      isSelf: true,
      deliveryState: 'sent',
    };
  }, [editingThreadReply, selectedConversationId, localAccountId]);
  const beginEditThreadReply = useCallback((reply: ThreadReply): void => {
    // Only text replies are editable (an edit rewrites the text body; a
    // media reply has no text to edit).
    const rawBody = reply.rawBody;
    if (rawBody !== undefined && envelopeKind(rawBody) !== 'com.ping.text') {
      pingToast.info({ title: 'Only text replies can be edited' });
      return;
    }
    void (async () => {
      const row = await db.messages.get(reply.id as unknown as string);
      setEditingThreadReply({
        id: reply.id as unknown as string,
        body: reply.rawBody ?? reply.body,
        contentHash: row?.contentHash ?? '',
      });
    })();
  }, []);
  const cancelEditThreadReply = useCallback((): void => setEditingThreadReply(null), []);
  const submitEditThreadReply = useCallback(
    async (newText: string): Promise<void> => {
      const target = editingThreadReply;
      const convId = selectedConversationId;
      const threadRoot = threadIdRef.current;
      if (target === null || convId === null) return;
      const trimmed = newText.trim();
      if (trimmed.length === 0) return;
      const conv = await resolveActiveConv({
        client,
        conversationId: convId,
        conversationName: selected?.name,
      });
      if (conv === null) throw new Error('Not connected — try again in a moment.');
      if (target.contentHash.length > 0) {
        await conv.send(
          encodeEnvelopeJsonBytes({
            kind: 'com.ping.edit',
            payload: { target: target.contentHash, body: trimmed },
          }),
        );
      }
      try {
        const newEnvelope = JSON.stringify({
          kind: 'com.ping.text',
          payload: { body: trimmed, thread: true },
          ...(threadRoot !== null ? { parent_id: threadRoot } : {}),
        });
        await webMessagesPersister.update(target.id, {
          body: newEnvelope,
          editedAt: Date.now(),
        });
      } catch (err) {
        log.warn({ event: 'chat.thread.edit.localUpdateFailed', err: (err as Error).message });
      }
      setEditingThreadReply(null);
    },
    [editingThreadReply, selectedConversationId, client, selected?.name],
  );

  // ── Retry a FAILED thread reply (parity with the main chat's bubble
  // retry). Re-sends the reply's ORIGINAL envelope bytes (so media +
  // `parent_id` survive), then swaps the failed local echo for a fresh
  // "sent" row so the tile flips failed → sent.
  const retryThreadReply = useCallback(
    (reply: ThreadReply): void => {
      const convId = selectedConversationId;
      if (convId === null) return;
      void (async () => {
        try {
          const conv = await resolveActiveConv({
            client,
            conversationId: convId,
            conversationName: selected?.name,
          });
          if (conv === null) return;
          const rawBody = reply.rawBody;
          const bytes =
            rawBody !== undefined && rawBody.charAt(0) === '{'
              ? new TextEncoder().encode(rawBody)
              : encodeEnvelopeJsonBytes({
                  kind: 'com.ping.text',
                  payload: { body: reply.body, thread: true },
                  parent_id: reply.threadId as unknown as string,
                });
          const sent = (await conv.send(bytes)) as {
            content_hash: Uint8Array;
            epoch: number | bigint;
          };
          const identity = await retrieveIdentity();
          await db.messages.delete(reply.id as unknown as string);
          if (identity !== null) {
            const sentAt = Date.now();
            await sharedPersistSentMessage({
              messages: webMessagesPersister,
              conversations: webConversationsPersister,
              record: {
                id:
                  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `local-retry-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                conversationId: convId,
                epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                serverSeq: sentAt,
                contentHash: bytesToHex(sent.content_hash),
                senderAccountId: identity.accountId,
                body:
                  rawBody ??
                  JSON.stringify({
                    kind: 'com.ping.text',
                    payload: { body: reply.body, thread: true },
                    parent_id: reply.threadId as unknown as string,
                  }),
                sentAt,
                deliveryState: 'sent',
              },
            });
          }
        } catch (err) {
          log.warn({ event: 'chat.thread.retry.failed', err: (err as Error).message });
        }
      })();
    },
    [selectedConversationId, client, selected?.name],
  );

  // Message-actions popover for thread replies (react / copy / pin /
  // save / forward / remove). Each action resolves the reply's
  // content-hash from Dexie (the `ThreadReply.id` is the local row id)
  // and `conv.send`s the matching envelope against the active
  // conversation — exactly like the main chat's actions.
  const threadRowActions = useMemo<ThreadRowActions>(() => {
    const memberAvatarUris = selected?.memberAvatarUris ?? {};
    const convId = selectedConversationId;
    const hashFor = async (reply: ThreadReply): Promise<string | null> => {
      const row = await db.messages.get(reply.id as unknown as string);
      return row?.contentHash && row.contentHash.length > 0 ? row.contentHash : null;
    };
    const sendToConv = async (bytes: Uint8Array): Promise<void> => {
      if (convId === null) return;
      const conv = await resolveActiveConv({
        client,
        conversationId: convId,
        conversationName: selected?.name,
      });
      if (conv === null) return;
      await conv.send(bytes);
    };
    // Toggle a reaction on a reply — optimistic local OR-set persist
    // (self-sends don't echo, so without this the sender's own pill would
    // never appear/disappear) + the matching wire envelope. Mirrors the
    // main chat's `handleToggleReactionForMessage`.
    const toggleReaction = (reply: ThreadReply, emoji: string): void => {
      const me = localAccountIdRef.current ?? '__local__';
      void (async () => {
        const target = await hashFor(reply);
        if (target === null) return;
        const replyId = reply.id as unknown as string;
        const current = reactionsByMessageId[replyId] ?? [];
        const reactedNow = current.find((r) => r.emoji === emoji)?.reactedByMe === true;
        const nextReacted = !reactedNow;
        try {
          if (nextReacted) {
            await webReactionsPersister.put({
              id: `${target}:${emoji}:${me}`,
              conversationId: convId ?? '',
              targetContentHash: target,
              targetMessageId: replyId,
              emoji,
              actorAccountId: me,
              addedAt: Date.now(),
            });
          } else {
            await webReactionsPersister.deleteByKey(convId ?? '', target, emoji, me);
          }
        } catch (err) {
          log.warn({
            event: 'chat.thread.reaction.persist.failed',
            err: (err as Error).message,
          });
        }
        try {
          await sendToConv(
            encodeEnvelopeJsonBytes({
              kind: nextReacted ? 'com.ping.reaction' : 'com.ping.reaction_remove',
              payload: { target, emoji, actorAccountId: me },
            }),
          );
        } catch (err) {
          log.warn({
            event: 'chat.thread.reaction.send.failed',
            err: (err as Error).message,
          });
        }
      })();
    };
    return {
      // Quick-react hover chip → toggle the default 👍 (parity with the
      // main chat's `handleReactPress`).
      onReact: (reply) => toggleReaction(reply, '👍'),
      onToggleReaction: (reply, emoji) => toggleReaction(reply, emoji),
      onMenuAction: (action: MessageMenuAction, reply) => {
        void (async () => {
          // Copy the DECODED text (parity with the main chat) — `reply.body` is
          // only the truncated list preview, so decode `rawBody` for the full
          // message. Toast so the action has the same feedback as the main chat.
          if (action === 'copy') {
            let text = reply.body;
            const raw = reply.rawBody;
            if (raw !== undefined && raw.length > 0) {
              const parsed = parseEnvelope(raw);
              if (parsed?.kind === 'com.ping.text') {
                text = (parsed.body as { body: string }).body;
              }
            }
            try {
              if (typeof navigator !== 'undefined' && navigator.clipboard) {
                await navigator.clipboard.writeText(text);
              }
              pingToast.success({ title: 'Message copied' });
            } catch {
              /* clipboard denied — best effort */
            }
            return;
          }
          const target = await hashFor(reply);
          if (action === 'remove') {
            // Mirror the main chat: MY OWN reply → redact for everyone + local
            // delete; SOMEONE ELSE'S reply → delete-for-me only (never broadcast
            // a redact for a peer's message). The local delete drops the row from
            // Dexie so the thread's liveQuery refreshes it out of view.
            const isOwn = reply.isSelf === true;
            const removeLocal = async (): Promise<void> => {
              try {
                if (target !== null && convId !== null) {
                  await addLocallyRemovedMessage(convId, target);
                }
                const row =
                  target !== null
                    ? await webMessagesPersister.getByContentHash(target)
                    : undefined;
                await webMessagesPersister.delete(row?.id ?? (reply.id as unknown as string));
              } catch {
                /* best effort */
              }
            };
            try {
              if (isOwn && target !== null) {
                await sendToConv(
                  encodeEnvelopeJsonBytes({
                    kind: 'com.ping.redact',
                    payload: { target, reason: 'user' },
                  }),
                );
              }
              await removeLocal();
            } catch {
              pingToast.error({
                title: "Couldn't remove",
                description: 'Try again in a moment.',
              });
            }
            return;
          }
          // The remaining actions need a resolved content hash.
          if (target === null) return;
          if (action === 'pin') {
            // Toggle against the committed pin set (shared localStorage key with
            // the main room) so a second tap unpins instead of re-pinning.
            const willPin =
              convId === null
                ? true
                : !(await webPinnedPersister.listByConversation(convId)).includes(target);
            if (convId !== null) await webPinnedPersister.set(convId, target, willPin);
            const identity = await retrieveIdentity();
            await sendToConv(
              encodeEnvelopeJsonBytes({
                kind: willPin ? 'com.ping.pin' : 'com.ping.unpin',
                payload: {
                  target,
                  ...(identity !== null ? { actorDisplayName: identity.displayName } : {}),
                },
              }),
            );
            pingToast.success({ title: willPin ? 'Message pinned' : 'Message unpinned' });
          } else if (action === 'save') {
            // Toggle against the committed saved set (mirrors the main room's
            // save/unsave) so the entry works both ways.
            const willSave =
              convId === null
                ? true
                : !(await webSavedPersister.listByConversation(convId)).includes(target);
            if (convId !== null) await webSavedPersister.set(convId, target, willSave);
            await sendToConv(
              encodeEnvelopeJsonBytes({
                kind: willSave ? 'com.ping.save' : 'com.ping.unsave',
                payload: { target },
              }),
            );
            pingToast.success({ title: willSave ? 'Message saved' : 'Removed from saved' });
          } else if (action === 'star') {
            // Star/unstar (§2.7 0x64) — DeviceGroup-synced flat set. Optimistic
            // local write + best-effort broadcast to the user's other devices
            // (parity with the main chat's `handleMenuAction` star case).
            const willStar = !(await webStarredPersister.list()).includes(target);
            await webStarredPersister.set(target, willStar);
            void sendStar(client, target, willStar).catch(() => undefined);
            pingToast.success({ title: willStar ? 'Message starred' : 'Removed from starred' });
          } else if (action === 'forward') {
            // Open the route-level forward picker seeded with this reply.
            setThreadForwardSource({
              body: reply.rawBody ?? reply.body,
              contentHash: target,
              senderId: reply.authorId as unknown as string,
            });
          }
          // 'replyInThread' stays hidden inside a thread (no nested threads).
        })();
      },
      reactionsByReplyId: reactionsByMessageId,
      // Reflect pinned/saved/starred state in the reply action menu so the
      // entries read Unpin/Unsave/Unstar (resolve the reply's hash → set).
      isReplyPinned: (reply: ThreadReply): boolean => {
        const h = threadHashByReplyId[reply.id];
        return h !== undefined && threadPinnedHashes.has(h);
      },
      isReplySaved: (reply: ThreadReply): boolean => {
        const h = threadHashByReplyId[reply.id];
        return h !== undefined && threadSavedHashes.has(h);
      },
      isReplyStarred: (reply: ThreadReply): boolean => {
        const h = threadHashByReplyId[reply.id];
        return h !== undefined && threadStarredHashes.has(h);
      },
      // Host-built variant — injects the SAME live context the main chat
      // uses so thread image/poll replies are fully functional. Returns
      // undefined for every other kind so the panel's context-free
      // `deriveVariantFromBody` fallback renders voice/doc/location/game.
      buildVariant: (reply: ThreadReply): ChatMessageVariant | undefined => {
        const rawBody = reply.rawBody;
        if (rawBody === undefined || rawBody.length === 0) return undefined;
        const kind = envelopeKind(rawBody);
        const replyId = reply.id as unknown as string;

        if (kind === 'com.ping.image') {
          const imageUri = threadImagePreviewByReplyId[replyId];
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            image: {
              onTapOpen: () => {
                if (imageUri !== undefined) setThreadLightboxUri(imageUri);
              },
              ...(imageUri !== undefined ? { imageUri } : {}),
            },
          });
        }

        if (kind === 'com.ping.poll') {
          const target = pollTargetByReplyId[replyId];
          if (target === undefined) {
            // Hash not resolved yet — let the display-only fallback render
            // the poll card until the contentHash lookup completes.
            return undefined;
          }
          const tally = threadPollVotesByTarget[target] ?? {
            counts: {},
            total: 0,
            votersByOption: {},
          };
          // Resolve each voter account id → real display name + avatar (parity
          // with the main chat's `resolvePollVoter`). Used for BOTH the tile's
          // per-option avatar stack AND the "Poll votes" panel — previously the
          // thread hardcoded "Member" with no avatar.
          const resolvedVotersByOption = resolveThreadVotersByOption(tally.votersByOption);
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            poll: {
              counts: tally.counts,
              ...(tally.myVote !== undefined ? { myVote: tally.myVote } : {}),
              ...(tally.myVotes !== undefined ? { myVotes: tally.myVotes } : {}),
              total: tally.total,
              votersByOption: resolvedVotersByOption,
              onVote: (optionIndex: number) => voteThreadPoll(target, optionIndex),
              onViewVotes: () => {
                const parsed = parseEnvelope(rawBody);
                if (parsed?.kind !== 'com.ping.poll') return;
                const b = parsed.body as {
                  question: string;
                  options: readonly string[];
                  multi_select?: boolean;
                  anonymous?: boolean;
                  closes_at?: number;
                };
                setThreadVotesPanel({
                  poll: {
                    pollTarget: target,
                    question: b.question,
                    options: b.options,
                    multipleChoice: b.multi_select === true,
                    ...(b.anonymous === true ? { anonymous: true } : {}),
                    ...(b.closes_at !== undefined ? { closesAt: b.closes_at } : {}),
                    creatorName: '',
                    createdAt: Date.now(),
                  },
                  counts: tally.counts,
                  total: tally.total,
                  votersByOption: resolvedVotersByOption,
                });
              },
            },
          });
        }

        // Meeting reply (`com.ping.meeting` created inside the thread). Inject
        // the SAME live context the main chat uses — folded `threadMeetingState`
        // (counts + our RSVP) + the RSVP handler — so the thread card is fully
        // interactive instead of a frozen display-only tile.
        if (kind === 'com.ping.meeting') {
          const parsed = parseEnvelope(rawBody);
          const meetingId =
            parsed?.kind === 'com.ping.meeting'
              ? (parsed.body as { meeting_id?: string }).meeting_id
              : undefined;
          const rec = meetingId !== undefined ? threadMeetingState.meetings[meetingId] : undefined;
          if (meetingId === undefined || rec === undefined) {
            // Not folded yet — let the display-only fallback render the card.
            return undefined;
          }
          const counts = meetingRsvpCounts(threadMeetingState, meetingId);
          const mine =
            localAccountId !== null
              ? meetingMyRsvp(threadMeetingState, meetingId, localAccountId)
              : undefined;
          const isCreator = reply.isSelf;
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            meeting: {
              status: meetingStatus(rec, Date.now()),
              counts,
              ...(mine !== undefined ? { myRsvp: mine } : {}),
              isCreator,
              title: rec.title,
              startsAt: rec.startsAt,
              endsAt: rec.endsAt,
              ...(rec.location !== undefined ? { location: rec.location } : {}),
              ...(rec.description !== undefined ? { description: rec.description } : {}),
              ...(rec.meetingLink !== undefined ? { meetingLink: rec.meetingLink } : {}),
              ...(rec.autoCall !== undefined ? { autoCall: { media: rec.autoCall.media } } : {}),
              onRsvp: (response: MeetingRsvpResponse) => handleThreadMeetingRsvp(meetingId, response),
              onOpenLink: handleThreadOpenMeetingLink,
              onCopyLink: handleThreadCopyMeetingLink,
              onShowResponses: () => setResponsesThreadMeetingId(meetingId),
              ...(rec.autoCall !== undefined
                ? { onJoinCall: (media: 'audio' | 'video') => handleThreadJoinMeetingCall(media) }
                : {}),
              ...(isCreator
                ? {
                    onEdit: () => handleThreadMeetingEdit(meetingId),
                    onCancelMeeting: () => handleThreadMeetingCancel(meetingId),
                  }
                : {}),
            },
          });
        }

        // Game replies (`com.ping.game_*`). The context-free
        // `deriveVariantFromBody` already produces a `game` variant
        // (kind + gameKind + sessionId) for game_create / game_move /
        // game_outcome — return it so the panel renders the tile. The
        // LIVE session + the interactive `onGameMove` are wired
        // separately via `gameSessionForReply` / `onGameMove` below, so
        // here we just need the variant to carry the sessionId.
        if (kind !== undefined && isGameKind(kind)) {
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
          });
        }

        // Voice — build the variant manually (like the main chat) so the
        // shared `useVoicePlayback` driver can actually PLAY it: the
        // context-free `deriveVariantFromBody` never wires
        // `resolveVoiceSource`, so a thread voice reply was mute.
        if (kind === 'com.ping.voice') {
          const payload = parseVoiceEnvelope(rawBody);
          if (payload === undefined) return undefined;
          const voiceVariant: ChatMessageVariant = {
            kind: 'voice',
            state: reply.isSelf ? 'sent' : 'received',
            durationMs: payload.durationMs,
            waveform: THREAD_PLACEHOLDER_WAVEFORM,
            progress: 0,
            onTogglePlay: () => undefined,
            // Resolve the audio from the reply's RAW envelope (which carries the
            // inline base64 bytes), NOT `message.body` — the thread synthesizes
            // `message.body` as the one-line PREVIEW ("🎤 Voice message"), so the
            // old `resolveThreadVoiceSource(message)` parsed the preview and
            // returned null → the note was mute. Closing over `rawBody` here is
            // what makes thread voice notes actually play.
            resolveVoiceSource: () => ({
              kind: 'macos-base64',
              base64: payload.bytes,
              mime: payload.mime,
            }),
            ...(payload.transcript !== undefined && payload.transcript.length > 0
              ? { transcript: payload.transcript }
              : {}),
            onPlaybackError: () => {
              pingToast.error({
                title: "Couldn't play voice note",
                description: 'This audio format may be unsupported on your browser.',
              });
            },
          };
          return voiceVariant;
        }

        // Video — inject the host-resolved (decrypted) poster + video
        // object URLs so the inline <VideoAttachmentCard> player works;
        // without them the tile only had a non-viewable envelope url.
        if (kind === 'com.ping.video') {
          const media = threadVideoByReplyId[replyId];
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            video: {
              onTapOpen: () => {
                if (media?.videoUri !== undefined) {
                  window.open(media.videoUri, '_blank', 'noopener,noreferrer');
                }
              },
              ...(media?.videoUri !== undefined ? { videoUri: media.videoUri } : {}),
              ...(media?.posterUri !== undefined ? { posterUri: media.posterUri } : {}),
            },
          });
        }

        // Doc — wire the tap-to-open so a thread doc reply opens (was a
        // dead no-op via the context-free fallback).
        if (kind === 'com.ping.file') {
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            doc: { onTapPreview: () => handleThreadDocTap(rawBody) },
          });
        }

        // Location / link previews: derive with the base context, then
        // patch in the host-owned open handlers (maps deep link / URL
        // open) + the location sharer avatar so taps work — parity with
        // the main chat's `messageVariants` patching.
        const derived = deriveVariantFromBody(rawBody, {
          message: threadReplyMessage(reply),
        });
        if (derived?.kind === 'location') {
          const sharerAvatarUrl = reply.isSelf
            ? localAccountId !== null
              ? memberAvatarUris[localAccountId]
              : undefined
            : reply.authorImageUri ?? undefined;
          const { lat, lng } = derived;
          return {
            ...derived,
            ...(sharerAvatarUrl !== undefined ? { sharerAvatarUrl } : {}),
            onOpen: () => {
              window.open(
                `https://maps.google.com/?q=${lat},${lng}`,
                '_blank',
                'noopener,noreferrer',
              );
            },
          };
        }
        if (derived?.kind === 'linkPreview') {
          const href = derived.url;
          return {
            ...derived,
            onOpen: () => {
              window.open(href, '_blank', 'noopener,noreferrer');
            },
          };
        }
        return derived;
      },
      // Resolve the live, folded session for a game reply by the
      // variant's sessionId so the thread tile is interactive (current
      // phase + moves) instead of a frozen create-phase card.
      gameSessionForReply: (reply: ThreadReply) => {
        const rawBody = reply.rawBody;
        if (rawBody === undefined || rawBody.length === 0) return undefined;
        const kind = envelopeKind(rawBody);
        if (kind === undefined || !isGameKind(kind)) return undefined;
        const sessionId = gameSessionIdFromBody(rawBody);
        if (sessionId === undefined) return undefined;
        return gameSessions.get(sessionId);
      },
      onGameMove: handleThreadGameMove,
      gameOpponentName: peerName,
      // Read receipts (task 2), edit (task 3), retry (task 4) — parity
      // with the main chat's MessageRow wiring.
      resolveReaderName: resolveThreadReaderName,
      resolveReaderAvatar: resolveThreadReaderAvatar,
      ...(threadIsGroup && threadGroupNonSelfCount > 0
        ? { groupNonSelfMemberCount: threadGroupNonSelfCount }
        : {}),
      onEditReply: beginEditThreadReply,
      onRetryReply: retryThreadReply,
      // Avatar tap → anchored profile popover; @mention tap → full profile
      // modal. Parity with the main chat (WebChatRoom's handleAvatarPress /
      // handleMentionPress): canonicalise the reply author / mention id to an
      // ACCOUNT id first (a peer's `authorId` is a device hash), else the
      // profile fetch 400s → "Profile unavailable".
      onAvatarPress: (userId: string, anchorRef: React.RefObject<unknown>) => {
        const t = resolveThreadProfileTarget(userId);
        if (t === null) return;
        vm.handlers.openMiniProfile(t.accountId as never, anchorRef);
      },
      onMentionPress: (userId: string) => {
        const t = resolveThreadProfileTarget(userId);
        if (t === null) return;
        vm.handlers.openUserProfile(t.accountId as never, t.hint);
      },
    };
  }, [
    client,
    selectedConversationId,
    selected?.name,
    selected?.memberAvatarUris,
    vm.handlers,
    reactionsByMessageId,
    threadImagePreviewByReplyId,
    threadVideoByReplyId,
    pollTargetByReplyId,
    threadPollVotesByTarget,
    voteThreadPoll,
    gameSessions,
    handleThreadGameMove,
    handleThreadDocTap,
    localAccountId,
    peerName,
    resolveThreadReaderName,
    resolveThreadReaderAvatar,
    threadIsGroup,
    threadGroupNonSelfCount,
    beginEditThreadReply,
    retryThreadReply,
    resolveThreadVotersByOption,
    resolveThreadProfileTarget,
    threadMeetingState,
    handleThreadMeetingRsvp,
    handleThreadMeetingCancel,
    handleThreadMeetingEdit,
    handleThreadJoinMeetingCall,
    handleThreadOpenMeetingLink,
    handleThreadCopyMeetingLink,
    threadHashByReplyId,
    threadPinnedHashes,
    threadSavedHashes,
    threadStarredHashes,
  ]);

  // When the user clicks a row in the list, push the friendliest
  // URL we can produce: for direct chats that's the peer's
  // account_id (matches the documented `/chat/<accountId>` shape);
  // for groups (no single peer) or rows missing a peerAccountId
  // we fall back to the local conv id so back/forward still works.
  const handleConversationSelect = useCallback(
    (nextLocalId: string): void => {
      const conv = conversations.find((c) => c.id === nextLocalId);
      const urlKey =
        conv?.kind === 'direct' && conv.peerAccountId
          ? conv.peerAccountId
          : nextLocalId;
      setRouteId(urlKey);
    },
    [conversations, setRouteId],
  );

  const handleConversationStarted = useCallback(
    (newLocalId: string): void => {
      // NewChatModal already wrote the local conv row; reuse the
      // same selection helper so URL routing stays consistent.
      handleConversationSelect(newLocalId);
    },
    [handleConversationSelect],
  );

  /**
   * Delete a conversation from the local store. Used by the trash
   * affordance on each conversation row to let the user restart an
   * invite/chat flow with the same peer.
   *
   * Today the work is local-only:
   *   • Purge every message row keyed by `conversationId` from Dexie
   *   • Drop the conversation row itself
   *   • Clear the URL selection if the deleted chat was active
   *
   * A future follow-up will also send an MLS "leave" + server-side
   * removal via /v1/conversations/<id> DELETE so peers see the chat
   * end. For now the user can re-invite the same peer through
   * NewChatModal and a fresh local conversation is created.
   */
  const handleConversationDelete = useCallback(
    (id: string): void => {
      void (async () => {
        try {
          await db.messages.where('conversationId').equals(id).delete();
          await deleteConversation(id);
          // If the user just deleted the open chat, drop the URL
          // selection so the empty state takes over.
          if (selectedConversationId === id) {
            setRouteId(null);
          }
          pingToast.success({ title: 'Conversation deleted' });
          log.info({
            event: 'chat.conversation.deleted',
            conversationIdPrefix: id.slice(0, 6),
          });
        } catch (err) {
          log.error({
            event: 'chat.conversation.delete.failed',
            err: (err as Error).message,
          });
          pingToast.error({
            title: "Couldn't delete conversation",
            description: 'Try again in a moment.',
          });
        }
      })();
    },
    [selectedConversationId, setRouteId],
  );

  const handleConversationMuteToggle = useCallback(
    (id: string, muted: boolean): void => {
      void (async () => {
        try {
          if (muted) await muteConversation(id);
          else await unmuteConversation(id);
        } catch (err) {
          log.warn({
            event: 'chat.conversation.muteToggle.failed',
            err: (err as Error).message,
          });
        }
      })();
    },
    [],
  );

  // When a poll / location / game overlay is opened FROM the thread
  // composer, this holds the thread root so the overlay's submit stamps
  // `parent_id`. Cleared after each submit (or when opened from main chat).
  const overlayThreadTargetRef = useRef<string | null>(null);

  // Web never shows the image editor — any legacy `openImageEditor` call
  // routes straight to multipart upload in the active `WebChatRoom`.
  const shellHandlers = useMemo(
    () => ({
      ...vm.handlers,
      openImageEditor: (sources: Parameters<typeof vm.handlers.openImageEditor>[0]) => {
        log.info({ event: 'chat.attach.image.direct', count: sources.length });
        uploadImageSources(sources);
      },
      // Composer `+ → Game` entry → open the game picker overlay
      // mounted at this route level (the VM has no game-overlay slot).
      openGamePicker: () => {
        setGamePickerOpen(true);
      },
    }),
    // The only runtime dependency is `vm.handlers` (spread + invoked above).
    // The lone bare `vm` the linter flags is inside a
    // `typeof vm.handlers.openImageEditor` TYPE annotation, not a runtime
    // value — depending on `vm` would recompute this (and re-render every
    // consumer) on each render for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vm.handlers],
  );

  // Thread-scoped composer: attachments / voice / GIFs / stickers send
  // INTO the open thread (parentId = thread root); poll / location /
  // games open the route-level overlays after flagging the thread target.
  const openThreadPoll = useCallback(() => {
    overlayThreadTargetRef.current = threadIdRef.current;
    vm.handlers.openPollComposer();
  }, [vm.handlers]);
  const openThreadLocation = useCallback(() => {
    overlayThreadTargetRef.current = threadIdRef.current;
    vm.handlers.openShareLocation();
  }, [vm.handlers]);
  const openThreadGames = useCallback(() => {
    overlayThreadTargetRef.current = threadIdRef.current;
    setGamePickerOpen(true);
  }, []);
  const openThreadMeeting = useCallback(() => {
    overlayThreadTargetRef.current = threadIdRef.current;
    vm.handlers.openMeetingComposer?.();
  }, [vm.handlers]);
  // @mention candidates for the thread composer — same roster the main
  // chat feeds its composer, so `@` autocompletes group members in a
  // thread reply too (previously the thread `@` picker had no candidates).
  const threadMentionCandidates = useMemo<ReadonlyArray<MentionCandidate>>(() => {
    const names = selected?.memberDisplayNames;
    if (names === undefined) return [];
    return Object.entries(names).map(([userId, displayName]) => ({ userId, displayName }));
  }, [selected?.memberDisplayNames]);
  const {
    composerProps: threadComposerProps,
    stagedAttachments: threadStagedAttachments,
    onRemoveStagedAttachment: onRemoveThreadStaged,
    onFlushStagedAttachments: onFlushThreadStaged,
  } = useThreadComposer({
    threadIdRef,
    conversationId: selectedConversationId,
    ...(selected?.name !== undefined ? { conversationName: selected.name } : {}),
    client,
    mentionCandidates: threadMentionCandidates,
    onOpenPoll: openThreadPoll,
    onOpenLocation: openThreadLocation,
    onOpenGames: openThreadGames,
    onOpenMeeting: openThreadMeeting,
  });
  // Merge in the edit-mode props (task 3). When a self reply is being
  // edited the composer shows the "Editing message" chip and routes Send
  // to `submitEditThreadReply` instead of posting a new reply; otherwise
  // `editingMessage` is null and the composer behaves normally.
  const threadComposerPropsWithEdit = useMemo<ThreadComposerProps>(
    () => ({
      ...threadComposerProps,
      editingMessage: editingThreadMessage,
      onSubmitEdit: submitEditThreadReply,
      onCancelEdit: cancelEditThreadReply,
    }),
    [threadComposerProps, editingThreadMessage, submitEditThreadReply, cancelEditThreadReply],
  );

  // Game picker → build the create body + fresh session, send the
  // `com.ping.game_create` envelope, then fold the optimistic local
  // create into the projection so the sender sees the game card.
  const handleGamePick = useCallback(
    (kind: Parameters<typeof createGame>[0]): void => {
      setGamePickerOpen(false);
      const convId = selectedConversationId;
      if (convId === null) return;
      // If the picker was opened from a thread composer, post the game
      // INTO that thread (parent_id) and clear the one-shot target.
      const threadTarget = overlayThreadTargetRef.current;
      overlayThreadTargetRef.current = null;
      void (async () => {
        try {
          const { sessionId, body } = createGame(kind);
          const conv = await resolveActiveConv({
            client,
            conversationId: convId,
            conversationName: selected?.name,
          });
          if (conv === null) return;
          const envelopeBody = {
            kind: 'com.ping.game_create' as const,
            // Thread reply → self-identify so the main timeline folds it out.
            payload: { ...body, ...(threadTarget !== null ? { thread: true } : {}) },
            ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
          };
          const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
            content_hash: Uint8Array;
            epoch: number | bigint;
          };
          // Persist the create as a LOCAL message row so the creator's
          // own timeline renders the interactive game tile (self-sends
          // don't echo via onMessage). The receiver gets its row via
          // useIncomingMessages. The tile is then driven by the live
          // useGameSessions fold as both players move.
          const identity = await retrieveIdentity();
          if (identity) {
            const sentAt = Date.now();
            await sharedPersistSentMessage({
              messages: webMessagesPersister,
              conversations: webConversationsPersister,
              record: {
                id:
                  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `local-game-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                conversationId: convId,
                epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                serverSeq: sentAt,
                contentHash: bytesToHex(sent.content_hash),
                senderAccountId: identity.accountId,
                body: JSON.stringify(envelopeBody),
                sentAt,
                deliveryState: 'sent',
                ...(threadTarget !== null
                  ? { inReplyTo: { messageId: threadTarget, preview: 'Game' } }
                  : {}),
              },
            });
          }
          applyGameEvent({
            type: 'create',
            sessionId,
            gameKind: kind,
            createdBy: '__self__',
          });
        } catch (err) {
          log.error({
            event: 'chat.attach.game.send.failed',
            err: (err as Error).message,
          });
          pingToast.error({
            title: "Couldn't start game",
            description: 'Check your connection and try again.',
          });
        }
      })();
    },
    [createGame, applyGameEvent, client, selectedConversationId, selected?.name],
  );

  useEffect(() => {
    if (vm.state.activeOverlays.includes('imageEditor')) {
      vm.handlers.closeOverlay('imageEditor');
    }
  }, [vm.state.activeOverlays, vm.handlers]);

  // Conversations list — placed left (default) or right per prefs. The left
  // column holds TWO scenes — the conversation list and the Activity feed —
  // side by side in a 200%-wide row, slid horizontally via a PERCENTAGE
  // transform (the header bell shows the feed; the feed's close button slides
  // back). Percentages (not measured pixels) mean the slide auto-tracks the
  // column width, so dragging the resize handle stays smooth on BOTH scenes.
  // Both scenes stay mounted (no re-fetch / scroll reset); the off-screen one is
  // click-disabled. No drag-to-swap.
  const conversationsPanel = (
    <div
      className="relative h-full shrink-0 overflow-hidden"
      style={{ width: sidePanelWidth }}
    >
      <div
        className="flex flex-row h-full"
        style={{
          width: '200%',
          transform: `translateX(${leftPanelTab === 1 ? '-50%' : '0%'})`,
          // Swap animation disabled — switch instantly between the conversation
          // list and the activity feed (no slide).
          transition: 'none',
        }}
      >
        <div
          className="h-full shrink-0 flex"
          style={{ width: '50%', pointerEvents: leftPanelTab === 0 ? 'auto' : 'none' }}
          aria-hidden={leftPanelTab !== 0}
        >
          <ConversationListPanel
            key="conversations"
            selectedConversationId={selectedConversationId}
            onConversationSelect={handleConversationSelect}
            onNewChatPress={() => setIsNewChatOpen(true)}
            {...(groupCreateEnabled
              ? { onNewGroupPress: () => setIsNewGroupOpen(true) }
              : {})}
            onConversationDelete={handleConversationDelete}
            onConversationMuteToggle={handleConversationMuteToggle}
            onCreateCompany={handleCreateCompany}
            onNotificationsPress={() => setLeftPanelTab(1)}
            notificationsBadgeCount={activityUnreadCount}
            hideResizeHandle
            fillWidth
          />
        </div>
        <div
          className="h-full shrink-0 flex"
          style={{ width: '50%', pointerEvents: leftPanelTab === 1 ? 'auto' : 'none' }}
          aria-hidden={leftPanelTab !== 1}
        >
          <ActivityPanel onClose={() => setLeftPanelTab(0)} />
        </div>
      </div>
      {/* Always-visible handle (the panel's own is suppressed) so the column is
          resizable on BOTH the conversations and activity scenes. */}
      <SidePanelResizeHandle />
    </div>
  );

  // Up to TWO utility panels can be open at once — one docked LEFT, one
  // docked RIGHT — each on its own configured side (Settings → Sidebar).
  // We pick the highest-precedence ACTIVE panel for EACH side independently
  // (thread > profile > notes > tasks) so e.g. Profile (left) and Notes
  // (right) render simultaneously. Previously a single precedence collapsed
  // everything to one slot, so a left-docked Profile hid a right-docked
  // Notes entirely.
  const threadActive = vm.state.threadIdentifier !== null;
  type UtilityKind = 'thread' | 'notes' | 'tasks';
  const activePanels: ReadonlyArray<{ kind: UtilityKind; side: 'left' | 'right' }> = [
    ...(threadActive ? [{ kind: 'thread' as const, side: threadsSide }] : []),
    ...(chatUtilityPanel === 'notes' ? [{ kind: 'notes' as const, side: notesSide }] : []),
    ...(chatUtilityPanel === 'tasks' ? [{ kind: 'tasks' as const, side: tasksSide }] : []),
  ];
  const PANEL_PRECEDENCE: Record<UtilityKind, number> = {
    thread: 0,
    notes: 2,
    tasks: 3,
  };
  const pickPanelForSide = (side: 'left' | 'right'): UtilityKind | undefined =>
    activePanels
      .filter((p) => p.side === side)
      .sort((a, b) => PANEL_PRECEDENCE[a.kind] - PANEL_PRECEDENCE[b.kind])[0]?.kind;
  const renderPanelInner = (kind: UtilityKind | undefined): JSX.Element | null => {
    switch (kind) {
      case 'thread':
        return (
          <ThreadPanelHost
            embedded
            vm={vm}
            gateway={threadGateway}
            rowActions={threadRowActions}
            composerProps={threadComposerPropsWithEdit}
            stagedAttachments={threadStagedAttachments}
            onRemoveStagedAttachment={onRemoveThreadStaged}
            onFlushStagedAttachments={onFlushThreadStaged}
          />
        );
      case 'notes':
        return <NotesPanelHost />;
      case 'tasks':
        return <TasksPanelHost />;
      default:
        return null;
    }
  };
  const makePanelFrame = (
    kind: UtilityKind | undefined,
    side: 'left' | 'right',
  ): JSX.Element | null => {
    const inner = renderPanelInner(kind);
    if (inner === null) return null;
    return (
      <SidePanelFrame
        key={`utility-${side}`}
        width={rightPanelWidth}
        minWidth={RIGHT_PANEL_MIN_WIDTH}
        maxWidth={RIGHT_PANEL_MAX_WIDTH}
        onResize={setRightPanelWidth}
        handleSide={side === 'right' ? 'left' : 'right'}
        accessibilityLabel="Side panel"
        className={`bg-light-surface-primary dark:bg-[#11131A] ${
          side === 'right'
            ? 'border-l border-light-border-subtle dark:border-dark-border-subtle'
            : 'border-r border-light-border-subtle dark:border-dark-border-subtle'
        }`}
      >
        {inner}
      </SidePanelFrame>
    );
  };
  const leftPanelEl = makePanelFrame(pickPanelForSide('left'), 'left');
  const rightPanelEl = makePanelFrame(pickPanelForSide('right'), 'right');

  return (
    <ChatRoomShellHandlersProvider handlers={shellHandlers}>
      {/*
        `h-full` (not `h-screen`) — AppShell already pins the
        outer container to viewport height and reserves 56px for the
        TopBar. Using `h-screen` here re-claims the full viewport,
        which over-extends past the main card's `overflow-hidden`
        boundary by the topbar height and visibly clips the composer
        off the bottom of the screen.
      */}
      {/* Chatroom profile popover + full modal (avatar tap → name tap). */}
      <ProfileOverlaysHost vm={vm} />
      <div className="flex flex-row w-full h-full min-h-0">
        {conversationsSide === 'left' ? conversationsPanel : null}
        {leftPanelEl}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-light-surface-primary dark:bg-[#0A0B0F]">
          {selectedConversationId !== null ? (
            <WebChatRoom
              conversationId={selectedConversationId}
              conversationName={selected?.name}
              onDeleteConversation={() => handleConversationDelete(selectedConversationId)}
            />
          ) : (
            <ChatEmptyState onNewChatPress={() => setIsNewChatOpen(true)} />
          )}
        </div>
        {rightPanelEl}
        {conversationsSide === 'right' ? conversationsPanel : null}

        <NewChatModal
          open={isNewChatOpen}
          onDismiss={() => {
            setIsNewChatOpen(false);
            setInviteAccountId(null);
            setInviteGroupJoin(null);
          }}
          onConversationStarted={(id) => {
            setInviteAccountId(null);
            setInviteGroupJoin(null);
            handleConversationStarted(id);
          }}
          {...(inviteAccountId !== null ? { autoStartAccountId: inviteAccountId } : {})}
          {...(inviteGroupJoin !== null ? { autoStartGroupJoin: inviteGroupJoin } : {})}
        />
        {groupCreateEnabled ? (
          <NewGroupModal
            open={isNewGroupOpen}
            onDismiss={() => setIsNewGroupOpen(false)}
            onConversationStarted={handleConversationStarted}
          />
        ) : null}
      </div>

      {/*
        Modal stack hosts (poll / location). Web sends photos directly
        from `WebChatRoom` (no image-editor overlay).
      */}
      <PollComposerHost
        vm={vm}
        onSubmitDraft={async (payload) => {
          log.info({
            event: 'chat.attach.poll.submit',
            optionCount: payload.state.options.length,
          });
          const convId = selectedConversationId;
          if (convId === null) {
            pingToast.error({
              title: "Couldn't create poll",
              description: 'Open a chat and try again.',
            });
            return;
          }
          try {
            const conv = await resolveActiveConv({
              client,
              conversationId: convId,
              conversationName: selected?.name,
            });
            if (conv === null) {
              pingToast.error({
                title: "Couldn't create poll",
                description: 'Open a chat and try again.',
              });
              return;
            }
            // Transform the composer's `PollComposerState` into the wire
            // `PollCreateBody` shape the decoder expects. The decoder reads
            // `options` as a STRING array and `multi_select` as a boolean —
            // shipping the raw `{id,text}[]` + `kind` would make the
            // receiver (and our own echo) drop every option silently.
            const pollState = payload.state;
            const closeMsByPreset: Record<string, number | undefined> = {
              '1h': 60 * 60 * 1000,
              '24h': 24 * 60 * 60 * 1000,
              '7d': 7 * 24 * 60 * 60 * 1000,
              never: undefined,
            };
            const closeDelta = closeMsByPreset[pollState.duration];
            // Thread target (set when opened from the thread composer).
            const threadTarget = overlayThreadTargetRef.current;
            overlayThreadTargetRef.current = null;
            const envelopeBody = {
              kind: 'com.ping.poll' as const,
              payload: {
                question: pollState.question,
                options: pollState.options
                  .map((o) => o.text.trim())
                  .filter((t) => t.length > 0),
                multi_select: pollState.kind === 'multiple',
                anonymous: pollState.anonymous,
                ...(closeDelta !== undefined
                  ? { closes_at: Date.now() + closeDelta }
                  : {}),
                // Thread reply → self-identify so the main timeline folds it out.
                ...(threadTarget !== null ? { thread: true } : {}),
              },
              ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
            };
            const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
              content_hash: Uint8Array;
              epoch: number | bigint;
            };
            // Persist a LOCAL message row so the creator's own timeline
            // shows the poll (self-sends don't echo via onMessage). The
            // contentHash matches peers so the vote tally aligns.
            const identity = await retrieveIdentity();
            if (identity) {
              const sentAt = Date.now();
              await sharedPersistSentMessage({
                messages: webMessagesPersister,
                conversations: webConversationsPersister,
                record: {
                  id:
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                      ? crypto.randomUUID()
                      : `local-poll-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                  conversationId: convId,
                  epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                  serverSeq: sentAt,
                  contentHash: bytesToHex(sent.content_hash),
                  senderAccountId: identity.accountId,
                  body: JSON.stringify(envelopeBody),
                  sentAt,
                  deliveryState: 'sent',
                  ...(threadTarget !== null
                    ? { inReplyTo: { messageId: threadTarget, preview: 'Poll' } }
                    : {}),
                },
              });
            }
          } catch (err) {
            log.error({
              event: 'chat.attach.poll.send.failed',
              err: (err as Error).message,
            });
            pingToast.error({
              title: "Couldn't create poll",
              description: 'Check your connection and try again.',
            });
          }
        }}
      />
      <MeetingComposerHost
        vm={vm}
        onSubmitDraft={async (draft) => {
          log.info({ event: 'chat.attach.meeting.submit', kind: draft.kind });
          const convId = selectedConversationId;
          if (convId === null) {
            pingToast.error({
              title: "Couldn't create meeting",
              description: 'Open a chat and try again.',
            });
            return;
          }
          try {
            const conv = await resolveActiveConv({
              client,
              conversationId: convId,
              conversationName: selected?.name,
            });
            if (conv === null) {
              pingToast.error({
                title: "Couldn't create meeting",
                description: 'Open a chat and try again.',
              });
              return;
            }
            // Thread target (set when the meeting composer was opened FROM the
            // thread composer). When present, stamp `parent_id` so the create
            // lands as a THREAD REPLY (the main timeline folds it out; the thread
            // renders its card) instead of a top-level message in the chatroom.
            const threadTarget = overlayThreadTargetRef.current;
            overlayThreadTargetRef.current = null;
            const meetingId = newMeetingId();
            const payload = buildMeetingCreatePayload({
              meetingId,
              title: draft.title,
              startsAt: draft.startsAt,
              endsAt: draft.endsAt,
              ...(draft.description !== undefined ? { description: draft.description } : {}),
              ...(draft.location !== undefined ? { location: draft.location } : {}),
              ...(draft.meetingLink !== undefined ? { meetingLink: draft.meetingLink } : {}),
              ...(draft.kind === 'call'
                ? { autoCall: { media: draft.autoCallMedia ?? 'audio', pre_invite_minutes: 0 } }
                : {}),
            });
            const envelopeBody = {
              kind: 'com.ping.meeting' as const,
              // Thread reply → self-identify so the main timeline folds it out.
              payload: { ...payload, ...(threadTarget !== null ? { thread: true } : {}) },
              ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
            };
            const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
              content_hash: Uint8Array;
              epoch: number | bigint;
            };
            // Self-echo: self-sends don't round-trip via `onMessage`, so persist
            // a local row (contentHash matches peers, keeping the RSVP fold
            // aligned) — the same pattern as poll create.
            const identity = await retrieveIdentity();
            if (identity) {
              const sentAt = Date.now();
              const contentHash = bytesToHex(sent.content_hash);
              await sharedPersistSentMessage({
                messages: webMessagesPersister,
                conversations: webConversationsPersister,
                record: {
                  id:
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                      ? crypto.randomUUID()
                      : `local-meeting-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                  conversationId: convId,
                  epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                  serverSeq: sentAt,
                  contentHash,
                  senderAccountId: identity.accountId,
                  body: JSON.stringify(envelopeBody),
                  sentAt,
                  deliveryState: 'sent',
                  ...(threadTarget !== null
                    ? { inReplyTo: { messageId: threadTarget, preview: 'Meeting' } }
                    : {}),
                },
              });
              // The local message row above IS the creator's create event —
              // WebChatRoom folds `meetingState` from the MESSAGES table (self
              // rows attributed to our account), so it sees US as the creator
              // (enabling creator-only edit/cancel + our RSVP). No separate
              // meeting-events store is needed.
              // Device-local reminder (§2.13) — the creator's "remind me N
              // minutes before" choice isn't on the wire, so schedule it here.
              // The AppShell-mounted `useMeetingReminderScheduler` fires it.
              if (draft.reminderMinutes !== undefined && draft.startsAt > sentAt) {
                await webMeetingRemindersPersister.put({
                  meetingId,
                  conversationId: convId,
                  title: draft.title,
                  startsAt: draft.startsAt,
                  reminderMinutes: draft.reminderMinutes,
                });
              }
            }
          } catch (err) {
            log.error({ event: 'chat.attach.meeting.send.failed', err: (err as Error).message });
            pingToast.error({
              title: "Couldn't create meeting",
              description: 'Check your connection and try again.',
            });
          }
        }}
      />
      {/* Edit + responses modals for a meeting rendered INSIDE A THREAD — the
          same shared components the main chat uses, so the in-thread card has
          identical edit / cancel / responses behaviour. */}
      {editingThreadMeeting !== null ? (
        <MeetingModal
          visible
          mode="edit"
          initial={editingThreadMeeting.initial}
          onClose={() => setEditingThreadMeeting(null)}
          onSubmit={(draft) => {
            submitThreadMeetingEdit(editingThreadMeeting.meetingId, draft);
            setEditingThreadMeeting(null);
          }}
        />
      ) : null}
      <MeetingResponsesModal
        visible={responsesThreadMeetingId !== null}
        onClose={() => setResponsesThreadMeetingId(null)}
        title={responsesThreadTitle}
        responses={threadMeetingResponses}
      />
      <ShareLocationHost
        vm={vm}
        {...(selected?.name !== undefined ? { conversationName: selected.name } : {})}
        onShare={async (result) => {
          log.info({ event: 'chat.attach.location.share' });
          try {
            const { location, liveDurationSeconds } = result;
            const threadTarget = overlayThreadTargetRef.current;
            overlayThreadTargetRef.current = null;
            const convId = selectedConversationId;
            const conv =
              convId === null
                ? null
                : await resolveActiveConv({
                    client,
                    conversationId: convId,
                    conversationName: selected?.name,
                  });
            if (conv === null || convId === null) {
              pingToast.error({
                title: "Couldn't share location",
                description: 'Open a chat and try again.',
              });
              return;
            }
            const envelopeBody = {
              kind: 'com.ping.location' as const,
              payload: {
                lat: location.lat,
                // Wire field is `lon` (see building-block contract).
                lon: location.lng,
                accuracy_m: 0,
                ...(location.label !== undefined ? { label: location.label } : {}),
                ...(result.kind === 'live' && liveDurationSeconds !== undefined
                  ? { live_duration_s: liveDurationSeconds }
                  : {}),
                // Thread reply → self-identify so the main timeline folds it out.
                ...(threadTarget !== null ? { thread: true } : {}),
              },
              ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
            };
            const sent = (await conv.send(
              encodeEnvelopeJsonBytes(envelopeBody),
            )) as { content_hash: Uint8Array; epoch: number | bigint };
            // Persist a LOCAL echo so the SENDER sees their own location
            // tile (self-sends don't round-trip through `onMessage`). The
            // contentHash matches peers so reactions/threads align.
            const identity = await retrieveIdentity();
            if (identity) {
              const sentAt = Date.now();
              await sharedPersistSentMessage({
                messages: webMessagesPersister,
                conversations: webConversationsPersister,
                record: {
                  id:
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                      ? crypto.randomUUID()
                      : `local-location-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
                  conversationId: convId,
                  epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
                  serverSeq: sentAt,
                  contentHash: bytesToHex(sent.content_hash),
                  senderAccountId: identity.accountId,
                  body: JSON.stringify(envelopeBody),
                  sentAt,
                  deliveryState: 'sent',
                  ...(threadTarget !== null
                    ? { inReplyTo: { messageId: threadTarget, preview: '📍 Location' } }
                    : {}),
                },
              });
            }
          } catch (err) {
            log.error({
              event: 'chat.attach.location.send.failed',
              err: (err as Error).message,
            });
            pingToast.error({
              title: "Couldn't share location",
              description: 'Check your connection and try again.',
            });
          }
        }}
        onSearch={(query, signal) => searchPlaces(query, signal)}
        onResolveCurrentLocation={(signal) => resolveCurrentLocation(signal)}
      />
      <GamePickerOverlay
        open={gamePickerOpen}
        onPick={handleGamePick}
        onClose={() => setGamePickerOpen(false)}
      />
      {/* Forward-message conversation picker — opened by a thread reply's
          action-menu Forward entry. */}
      <ForwardOverlay
        open={threadForwardSource !== null}
        conversations={threadForwardTargets}
        onPick={handleThreadForwardPick}
        onClose={() => setThreadForwardSource(null)}
      />
      {/* Fullscreen lightbox for thread image replies — opened by the
          thread image variant's onTapOpen. */}
      <ImageLightbox
        open={threadLightboxUri !== null}
        {...(threadLightboxUri !== null ? { imageUri: threadLightboxUri } : {})}
        onClose={() => setThreadLightboxUri(null)}
      />
      {threadVotesPanel !== null ? (
        <PollVotesPanel
          visible
          poll={threadVotesPanel.poll}
          counts={threadVotesPanel.counts}
          total={threadVotesPanel.total}
          {...(threadVotesPanel.votersByOption !== undefined
            ? { votersByOption: threadVotesPanel.votersByOption }
            : {})}
          onClose={() => setThreadVotesPanel(null)}
        />
      ) : null}
      {/* Thread / Notes / Tasks now render IN-FLOW in the resizable
          right-utility panel (see `rightPanelEl` above) — no longer a
          fixed-position overlay. */}
    </ChatRoomShellHandlersProvider>
  );
}

// Empty thread snapshot for the no-conversation / not-found case — the
// panel renders "no replies yet" with a synthetic parent.
function emptyThreadSnapshot(identifier: ThreadIdentifier): ThreadOpenSnapshot {
  return {
    parent: {
      messageId: identifier as unknown as ThreadParentPreview['messageId'],
      authorId: '' as unknown as ThreadParentPreview['authorId'],
      authorName: '',
      previewText: '',
      timestamp: Date.now(),
      deleted: false,
    },
    replies: [],
    participants: [],
  };
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

// Placeholder waveform for a thread voice reply — the shared
// `useVoicePlayback` hook overrides it with the real decoded amplitude
// buckets once the AudioContext resolves. Mirrors WebChatRoom's
// `PLACEHOLDER_WAVEFORM`.
const THREAD_PLACEHOLDER_WAVEFORM: readonly number[] = [
  0.3, 0.5, 0.7, 0.9, 0.6, 0.4, 0.5, 0.8, 0.7, 0.5, 0.3, 0.6, 0.9, 0.7, 0.5, 0.4, 0.6, 0.8, 0.5,
  0.3, 0.4, 0.6, 0.5, 0.3,
];

// Cheap envelope-kind probe — parses the reply body just enough to route
// it to the right host-built variant branch. Returns undefined for
// non-JSON / unparseable bodies.
function envelopeKind(body: string): string | undefined {
  if (body.length === 0 || body.charAt(0) !== '{') return undefined;
  try {
    const parsed = JSON.parse(body) as { kind?: unknown };
    return typeof parsed.kind === 'string' ? parsed.kind : undefined;
  } catch {
    return undefined;
  }
}

// Pull the hex `session_id` out of a `com.ping.game_*` reply body so the
// host can resolve the live folded session from `useGameSessions`.
// Returns undefined for non-game / malformed bodies.
function gameSessionIdFromBody(body: string): string | undefined {
  if (body.length === 0 || body.charAt(0) !== '{') return undefined;
  try {
    const parsed = JSON.parse(body) as { payload?: { session_id?: unknown } };
    const sid = parsed.payload?.session_id;
    return typeof sid === 'string' && sid.length > 0 ? sid : undefined;
  } catch {
    return undefined;
  }
}

// Builds the `Message` the variant pipeline needs from a thread reply.
// Mirrors the shape ThreadOpenPanel synthesizes per row so the derived
// variant matches what the panel itself would produce.
function threadReplyMessage(reply: ThreadReply): Message {
  return {
    id: reply.id as unknown as string,
    conversationId: reply.threadId as unknown as string,
    senderId: reply.authorId as unknown as string,
    body: reply.body,
    hlc: '',
    contentHash: '',
    timestamp: reply.timestamp,
    isSelf: reply.isSelf,
    deliveryState: reply.status === 'failed' ? 'failed' : reply.status === 'sending' ? 'pending' : 'sent',
  };
}
